import React, { useCallback, useEffect, useRef, useState } from "react";
import styles from "./CRM.module.css";
import {
  Button,
  Menu,
  MenuTrigger,
  MenuList,
  MenuItem,
  MenuPopover,
  CounterBadge,
  MenuButton,
  Tab, TabList,
  Persona,
  InfoLabel,
  Link,
  MessageBarTitle,
  MessageBar,
  MessageBarBody,
  Tooltip,
  Input,
  Divider,
  Checkbox,
  Textarea,
} from "@fluentui/react-components";
import { List, ListItem } from "@fluentui/react-components";
import {
  AlertBadgeRegular,
  ArrowDownloadRegular,
  CalendarMonthRegular,
  DocumentArrowDownRegular,
  DocumentDataLinkRegular,
  DocumentFlowchartRegular,
  LinkRegular,
  SettingsRegular,
  MicRegular,
  Settings24Regular,
  Settings20Regular,
  Settings16Regular,
  FilterRegular,
  Add24Regular,
  ChevronDown16Regular,
  Pin16Regular,
  Timer16Regular,
  TopSpeedRegular,
  NoteEditRegular,
  AppTitleRegular,
  PersonAccountsRegular,
  ContactCardLinkRegular,
  SlideTextCallRegular,
  NoteAddRegular,
  FolderPeopleRegular,
  MailArrowForward16Regular,
  MoreHorizontalRegular,
  Share16Regular,
  Save16Regular,
  Add16Regular,
  BookDismiss16Regular,
  PeopleAdd16Regular,
  FolderList16Regular,
  PersonAdd16Regular,
  Delete16Regular,
  Mail16Regular,
  Phone16Regular,
  Info16Regular,
  SendCopyRegular,
  CommentBadgeRegular,
  MailArrowUp16Regular,
  CommentBadge16Regular,
  CommentEdit16Regular,
  MailArrowUpRegular,
  CommentEditRegular,
  Calendar16Regular,
  CallDismiss16Regular,
  CheckmarkCircle16Regular,
  CheckmarkCircleRegular,
  CallDismissRegular,
  NoteAdd16Regular,
  Note16Regular,
  AddRegular,
  Edit16Regular,
  CalendarClockRegular,
  ChevronRight16Regular,
  ChevronLeft16Regular,
  BuildingTownhouseRegular,
  TimerRegular,
  LocationRegular,
  Location16Regular,
} from "@fluentui/react-icons";
import { SearchBox } from "@fluentui/react-search-preview";
import ContactFormCRM from "./ContactForm/ContactForm";
import crm from "../../assets/images/CRM.png";
import { format, isToday, parseISO, parse } from 'date-fns';
import { CalendarMonth } from "@fluentui/react-calendar-compat";
import { after } from "node:test";
import { apiCall } from "../../api/services";
import useAuth from "../../hooks/useAuth";
import { WorkflowTab } from "../../types/workflowTabs";
import CrmDashboard from "./dashboard/crmDashboard";
import { CrmBackendProvider, useCrmBackend } from "./Crmbackend";
import CrmExplorer, { CrmEntityView, Skeleton } from "./Crmexplorer";
import ex from "./Crmexplorer.module.css";
import type { CrmClient } from "./Api/crmClient";
import { withCalendar } from "./Api/Crmcalendar";
import { sourceOf } from "../../types/Crmrealtime";
import { useMemo } from "react";
import {
  Dialog, DialogSurface, DialogBody, DialogTitle, DialogContent, DialogActions, DialogTrigger, Field, Select,
} from "@fluentui/react-components";
import {
  Add20Regular, Alert20Regular, ArrowDownload20Regular, ArrowRight20Regular, ArrowUndo20Regular, BookRegular, CalendarClock20Regular,
  ChartMultipleRegular, Checkmark20Regular, ChevronDown12Regular, ChevronUp12Regular, Copy20Regular, DataFunnelRegular, Delete20Regular,
  Dismiss20Regular, Dismiss24Regular, Edit20Regular, HeadsetRegular, HistoryRegular, MegaphoneRegular, PeopleListRegular,
  PeopleTeamRegular, Phone20Regular, Send20Regular, Sparkle20Regular, SparkleRegular,
} from "@fluentui/react-icons";


/** The people in the Sales accelerator list: the contacts loaded from the CRM service, most recently touched first. */
const tabsOf = (d: Data): WorkflowTab[] => {
  const accts = new Map(rows(d, "accounts").map((a) => [String(a.id), String(a.name)]));
  return rows(d, "contacts").slice(0, 40).map((c, i) => ({
    id: String(c.id), name: String(c.name ?? ""), role: String(c.title ?? ""), company: accts.get(String(c.account)) ?? String(c.company ?? ""),
    datetime: new Date(Date.now() - i * 3600000).toISOString().slice(0, 19), avatar: "",
  }));
};


/* =====================================================================
   CRM modules (everything except Sales accelerator and Dashboard)
   ===================================================================== */
type Val = string | number;
interface Row {
  id: string;
  [k: string]: Val;
}
type Data = Record<string, Row[]>;
interface Opt {
  v: string;
  l: string;
}
interface Fld {
  key: string;
  label: string;
  kind: "text" | "number" | "date" | "select";
  options?: Opt[] | ((d: Data) => Opt[]);
  required?: boolean;
  full?: boolean;
}
interface Col {
  key: string;
  label: string;
  kind?: "money" | "date" | "status" | "num" | "pct" | "mono" | "bar";
  get?: (r: Row, d: Data) => Val;
  right?: boolean;
}
interface Draft {
  title: string;
  body: string;
  account?: string;
}
interface Res {
  patch?: Record<string, Val>;
  other?: { ent: string; id: string; patch: Record<string, Val> }[];
  creates?: { ent: string; id?: string; values: Record<string, Val> }[];
  draft?: Draft;
  msg: string;
  err?: string;
}
interface Act {
  label: string;
  icon: React.ReactNode;
  show: (r: Row) => boolean;
  run: (r: Row, d: Data, v: Record<string, Val>) => Res;
  prompt?: { title: string; submit: string; fields: Fld[]; init: (r: Row, d: Data) => Record<string, Val> };
}
interface Tile {
  label: string;
  value: string;
  note?: string;
}
interface Entity {
  id: string;
  src: string;
  title: string;
  subtitle: string;
  noun: string;
  page: "grid" | "board" | "forecast" | "ai";
  prefix?: string;
  initial?: Record<string, Val>;
  cols: Col[];
  fields: Fld[];
  acts: Act[];
  readOnly?: boolean;
  editable?: string[];
  filterKey?: string;
  filterLabel?: string;
  validate?: (v: Record<string, Val>, d: Data, existing: Row | null) => string | null;
  canDelete?: (r: Row, d: Data) => string | null;
  summary?: (list: Row[], d: Data) => Tile[];
  detail?: (r: Row, d: Data) => React.ReactNode;
}

/* ---------- helpers ---------- */
const NGN = new Intl.NumberFormat("en-NG", { style: "currency", currency: "NGN", maximumFractionDigits: 0 });
const money = (n: number) => NGN.format(Math.round(n) || 0);
const compact = (n: number) => (Math.abs(n) >= 1e6 ? `₦${(n / 1e6).toFixed(1)}M` : Math.abs(n) >= 1e3 ? `₦${Math.round(n / 1e3)}K` : money(n));
const pad2 = (n: number) => String(n).padStart(2, "0");
const isoD = (dt: Date) => `${dt.getFullYear()}-${pad2(dt.getMonth() + 1)}-${pad2(dt.getDate())}`;
const parseD = (s: string) => {
  const [y, m, dd] = s.split("-").map(Number);
  return new Date(y || 0, (m || 1) - 1, dd || 1);
};
const addDays = (s: string, n: number) => {
  const dt = parseD(s);
  dt.setDate(dt.getDate() + n);
  return isoD(dt);
};
const off = (n: number) => addDays(isoD(new Date()), n);
const fdate = (s: string) => (s ? parseD(s).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }) : "");
const daysBetween = (a: string, b: string) => Math.round((parseD(b).getTime() - parseD(a).getTime()) / 86400000);
const num = (v: Val | undefined): number => (typeof v === "number" ? v : Number(v) || 0);
const rows = (d: Data, k: string): Row[] => d[k] ?? [];
const sum = (list: Row[], f: (r: Row) => number) => list.reduce((t, r) => t + f(r), 0);
const R = (id: string, o: Record<string, Val>): Row => ({ id, ...o });
const opts = (...a: string[]): Opt[] => a.map((v) => ({ v, l: v }));
const cap = (s: string) => `${s[0]?.toUpperCase() ?? ""}${s.slice(1)}`;
const nameOf = (d: Data, k: string, id: Val | undefined) => String(rows(d, k).find((r) => r.id === String(id))?.name ?? id ?? "");
const nextId = (prefix: string, list: Row[]): string => {
  const mx = list.reduce((m, r) => Math.max(m, Number(r.id.slice(prefix.length)) || 0), 0);
  return prefix + String(mx + 1).padStart(4, "0");
};
const PFX: Record<string, string> = { accounts: "AC-", contacts: "CT-", opportunities: "OP-", activities: "AT-", orders: "SO-", invoices: "IV-" };

const TONE: Record<string, string> = {
  Active: "green", Customer: "green", Won: "green", Healthy: "green", Published: "green", Accepted: "green", Fulfilled: "green", Paid: "green", Resolved: "green", Completed: "green", Low: "green", Qualified: "green", Converted: "green", "On track": "green",
  New: "blue", Prospect: "blue", Open: "blue", Qualify: "blue", Develop: "blue", Sent: "blue", "In progress": "blue", Cold: "blue", Email: "blue", Launching: "blue",
  Contacted: "amber", Propose: "amber", Negotiate: "amber", "Needs attention": "amber", Warm: "amber", Medium: "amber", Unpaid: "amber", Escalated: "amber", Draft: "grey", Inactive: "grey",
  "At risk": "red", Hot: "red", Overdue: "red", Breached: "red", High: "red", Lost: "red", Disqualified: "red", Rejected: "red", Churned: "red", Expired: "red", Cancelled: "grey",
};
const Pill: React.FC<{ text: string }> = ({ text }) => <span className={`${styles.crPill} ${styles["crTone_" + (TONE[text] || "grey")]}`}>{text}</span>;

/* ---------- CRM intelligence (local rules, see askAssistant) ---------- */
const STAGES = ["Qualify", "Develop", "Propose", "Negotiate", "Won", "Lost"];
const PROB: Record<string, number> = { Qualify: 10, Develop: 25, Propose: 50, Negotiate: 75, Won: 100, Lost: 0 };
const isOpenOpp = (r: Row) => r.stage !== "Won" && r.stage !== "Lost";
const prob = (r: Row) => PROB[String(r.stage)] ?? 0;
const lastTouch = (d: Data, account: Val | undefined): number => {
  const done = rows(d, "activities").filter((a) => a.account === account && a.status === "Completed").map((a) => String(a.date));
  if (!done.length) return 99;
  return daysBetween(done.sort().reverse()[0] ?? off(-99), off(0));
};
const health = (r: Row, d: Data): string => {
  if (!isOpenOpp(r)) return "";
  const idle = lastTouch(d, r.account);
  if (String(r.close) < off(0) || idle > 14) return "At risk";
  if (idle > 7) return "Needs attention";
  return "Healthy";
};
const nextAction = (r: Row, d: Data): string => {
  if (!isOpenOpp(r)) return "No action needed. This deal is closed.";
  const idle = lastTouch(d, r.account);
  if (String(r.close) < off(0)) return `Close date has passed. Agree a new date with ${nameOf(d, "accounts", r.account)} or close the deal as lost.`;
  if (idle > 14) return `No contact for ${idle >= 99 ? "a long time" : idle + " days"}. Call ${nameOf(d, "accounts", r.account)} today and log it.`;
  if (r.stage === "Qualify") return "Confirm budget and decision maker, then move to Develop.";
  if (r.stage === "Develop") return "Run a discovery call and send a tailored proposal.";
  if (r.stage === "Propose") return "Send a quote and book a follow-up within 3 days.";
  return "Resolve final objections and ask for the signature.";
};
const SOURCE_PTS: Record<string, number> = { Referral: 25, Event: 20, Website: 15, Social: 10, "Cold call": 8 };
const leadScore = (r: Row, d: Data): { score: number; why: string[] } => {
  const why: string[] = [];
  let s = SOURCE_PTS[String(r.source)] ?? 5;
  why.push(`${String(r.source)} source +${s}`);
  const st = r.status === "Qualified" ? 30 : r.status === "Contacted" ? 10 : 0;
  if (st) {
    s += st;
    why.push(`${String(r.status)} +${st}`);
  }
  const b = num(r.budget);
  const bp = b >= 5e6 ? 25 : b >= 2e6 ? 15 : b >= 5e5 ? 8 : 0;
  if (bp) {
    s += bp;
    why.push(`Budget ${compact(b)} +${bp}`);
  }
  const touches = rows(d, "activities").filter((a) => a.lead === r.id).length;
  if (touches) {
    s += Math.min(3, touches) * 5;
    why.push(`${touches} logged touches +${Math.min(3, touches) * 5}`);
  }
  if (daysBetween(String(r.created), off(0)) <= 14) {
    s += 10;
    why.push("Created in the last 14 days +10");
  }
  return { score: Math.min(100, s), why };
};
const scoreLabel = (n: number) => (n >= 70 ? "Hot" : n >= 45 ? "Warm" : "Cold");

const accountSummary = (a: Row, d: Data): string => {
  const opps = rows(d, "opportunities").filter((o) => o.account === a.id);
  const open = opps.filter(isOpenOpp);
  const won = opps.filter((o) => o.stage === "Won");
  const cases = rows(d, "cases").filter((c) => c.account === a.id && c.status !== "Resolved");
  const idle = lastTouch(d, a.id);
  const parts = [`${String(a.name)} is a ${String(a.industry).toLowerCase() || "business"} account in ${String(a.city) || "Nigeria"}, currently ${String(a.status).toLowerCase()}.`];
  parts.push(open.length ? `${open.length} open ${open.length === 1 ? "deal" : "deals"} worth ${money(sum(open, (o) => num(o.amount)))}.` : "No open deals.");
  if (won.length) parts.push(`${money(sum(won, (o) => num(o.amount)))} won to date.`);
  if (cases.length) parts.push(`${cases.length} unresolved support ${cases.length === 1 ? "case" : "cases"}.`);
  parts.push(idle >= 99 ? "No completed activity logged yet." : idle > 14 ? `Last contact was ${idle} days ago, so the relationship is cooling.` : `Last contact was ${idle} days ago.`);
  return parts.join(" ");
};

const draftEmail = (name: string, company: string, topic: string, sender: string): Draft => ({
  title: `Follow-up to ${name}`,
  account: company,
  body: `Subject: Following up on ${topic}\n\nHi ${name.split(" ")[0] ?? name},\n\nThank you for your time recently. I wanted to follow up on ${topic} and see how things are progressing at ${company}.\n\nWould you be free for a short call this week to agree the next steps? I can share a tailored proposal beforehand if that helps.\n\nBest regards,\n${sender}`,
});

const insights = (d: Data): string[] => {
  const out: string[] = [];
  const open = rows(d, "opportunities").filter(isOpenOpp);
  const risky = open.filter((o) => health(o, d) === "At risk");
  if (risky.length) out.push(`${risky.length} open ${risky.length === 1 ? "deal" : "deals"} worth ${money(sum(risky, (o) => num(o.amount)))} ${risky.length === 1 ? "is" : "are"} at risk. Start with ${String(risky.sort((a, b) => num(b.amount) - num(a.amount))[0]?.name)}.`);
  const hot = rows(d, "leads").filter((l) => !["Converted", "Disqualified"].includes(String(l.status)) && leadScore(l, d).score >= 70);
  if (hot.length) out.push(`${hot.length} hot ${hot.length === 1 ? "lead is" : "leads are"} waiting: ${hot.slice(0, 3).map((l) => String(l.name)).join(", ")}.`);
  const closing = open.filter((o) => String(o.close) <= off(14) && String(o.close) >= off(0));
  if (closing.length) out.push(`${closing.length} ${closing.length === 1 ? "deal closes" : "deals close"} in the next 14 days, worth ${money(sum(closing, (o) => num(o.amount)))}.`);
  const breached = rows(d, "cases").filter((c) => c.status !== "Resolved" && String(c.due) < off(0));
  if (breached.length) out.push(`${breached.length} support ${breached.length === 1 ? "case has" : "cases have"} breached SLA.`);
  const overdue = rows(d, "activities").filter((a) => a.status === "Open" && String(a.date) < off(0));
  if (overdue.length) out.push(`${overdue.length} overdue ${overdue.length === 1 ? "task" : "tasks"} need attention.`);
  if (!out.length) out.push("Everything looks healthy. Keep the pipeline moving.");
  return out;
};

/**
 * Local "AI" assistant. It answers from your CRM data with simple intent rules so it works
 * offline. To use a real model, replace the body of this function with a call to your backend
 * (send `q` plus a data summary, return the text).
 */
const askAssistant = (q: string, d: Data): string => {
  const t = q.toLowerCase();
  const open = rows(d, "opportunities").filter(isOpenOpp);
  const find = <T extends Row>(list: T[], key = "name") => list.find((x) => t.includes(String(x[key]).toLowerCase()) || t.includes(String(x[key]).toLowerCase().split(" ")[0] ?? "#"));
  const acct = rows(d, "accounts").find((a) => t.includes(String(a.name).toLowerCase()));
  const lead = rows(d, "leads").find((l) => t.includes(String(l.name).toLowerCase()));
  const contact = rows(d, "contacts").find((c) => t.includes(String(c.name).toLowerCase()));
  if (/draft|write|email/.test(t)) {
    const who = lead ?? contact ?? find(rows(d, "leads")) ?? find(rows(d, "contacts"));
    if (who) {
      const company = lead || (who as Row).company ? String((who as Row).company ?? "") : nameOf(d, "accounts", (who as Row).account);
      return draftEmail(String(who.name), company, "our recent conversation", "Your sales team").body;
    }
    return "Tell me who the email is for, for example: Draft an email to Femi Balogun.";
  }
  if (/risk|stuck|stalled|slipping/.test(t)) {
    const r = open.filter((o) => health(o, d) === "At risk");
    return r.length ? `At-risk deals:\n${r.map((o) => `• ${String(o.name)} (${money(num(o.amount))}, ${String(o.stage)}). ${nextAction(o, d)}`).join("\n")}` : "No deals are at risk right now.";
  }
  if (/today|priorit|what should|plan my|focus/.test(t)) {
    const tasks = rows(d, "activities").filter((a) => a.status === "Open" && String(a.date) <= off(0));
    const lines = [...tasks.slice(0, 5).map((a) => `• ${String(a.type)}: ${String(a.subject)} (${String(a.date) < off(0) ? "overdue" : "today"})`), ...insights(d).slice(0, 3).map((s) => `• ${s}`)];
    return `Your priorities:\n${lines.join("\n")}`;
  }
  if (/hot|best lead|top lead|score|leads/.test(t)) {
    const ranked = rows(d, "leads").filter((l) => !["Converted", "Disqualified"].includes(String(l.status))).map((l) => ({ l, s: leadScore(l, d).score })).sort((a, b) => b.s - a.s).slice(0, 5);
    return `Top leads by score:\n${ranked.map((x) => `• ${String(x.l.name)}, ${String(x.l.company)}: ${x.s}/100 (${scoreLabel(x.s)})`).join("\n")}`;
  }
  if (/forecast|pipeline|revenue|weighted/.test(t)) {
    const w = sum(open, (o) => (num(o.amount) * prob(o)) / 100);
    return `Open pipeline is ${money(sum(open, (o) => num(o.amount)))} across ${open.length} deals. Weighted forecast is ${money(w)}. ${money(sum(rows(d, "opportunities").filter((o) => o.stage === "Won"), (o) => num(o.amount)))} has already been won.`;
  }
  if (/case|support|ticket|sla/.test(t)) {
    const c = rows(d, "cases").filter((x) => x.status !== "Resolved");
    return c.length ? `${c.length} open cases, ${c.filter((x) => String(x.due) < off(0)).length} past SLA:\n${c.slice(0, 5).map((x) => `• ${String(x.id)} ${String(x.title)} (${String(x.priority)})`).join("\n")}` : "There are no open support cases.";
  }
  if (/campaign|marketing/.test(t)) {
    const c = rows(d, "campaigns").filter((x) => num(x.spent) > 0).map((x) => ({ x, roi: (num(x.revenue) - num(x.spent)) / num(x.spent) })).sort((a, b) => b.roi - a.roi)[0];
    return c ? `Best campaign by return is ${String(c.x.name)}: ${Math.round(c.roi * 100)}% ROI on ${money(num(c.x.spent))} spent.` : "No campaign has spend yet.";
  }
  if (/quota|target|attain|team/.test(t)) {
    const team = rows(d, "team").filter((m) => num(m.quota) > 0);
    return `Quota attainment:\n${team.map((m) => `• ${String(m.name)}: ${teamAttain(m, d)}%`).join("\n")}`;
  }
  if (acct) return accountSummary(acct, d);
  if (/summar|tell me about|about/.test(t)) return "Which account should I summarise? For example: Summarise Zenith Bank.";
  return "I can help with: today's priorities, at-risk deals, top leads, the forecast, an account summary, a draft email, support cases, campaigns and quota attainment. Try one of the suggestions.";
};

const teamAttain = (m: Row, d: Data): number => {
  const won = sum(rows(d, "opportunities").filter((o) => o.stage === "Won" && o.owner === m.name), (o) => num(o.amount));
  return num(m.quota) ? Math.round((won / num(m.quota)) * 100) : 0;
};

/* ---------- engine ---------- */
const audit = (d: Data, entTitle: string, rec: string, action: string, detail: string): Data => ({
  ...d,
  audit: [R(`AU-${String(rows(d, "audit").length + 1).padStart(4, "0")}`, { when: new Date().toLocaleString("en-GB"), user: "You", entity: entTitle, record: rec, action, detail }), ...rows(d, "audit")],
});
const apply = (d: Data, src: string, id: string, res: Res): Data => {
  const next: Data = { ...d };
  next[src] = rows(d, src).map((r) => (r.id === id ? ({ ...r, ...(res.patch ?? {}) } as Row) : r));
  (res.other ?? []).forEach((o) => {
    next[o.ent] = rows(next, o.ent).map((r) => (r.id === o.id ? ({ ...r, ...o.patch } as Row) : r));
  });
  (res.creates ?? []).forEach((c) => {
    const list = rows(next, c.ent);
    next[c.ent] = [...list, { ...c.values, id: c.id ?? nextId(PFX[c.ent] ?? "", list) } as Row];
  });
  return next;
};

const setStatus = (label: string, icon: React.ReactNode, from: string[], to: string, msg: (r: Row) => string): Act => ({
  label,
  icon,
  show: (r) => from.includes(String(r.status)),
  run: (r) => ({ patch: { status: to }, msg: msg(r) }),
});
const toggleActs = (nameKey: string): Act[] => [
  setStatus("Deactivate", <Dismiss20Regular />, ["Active"], "Inactive", (r) => `${String(r[nameKey])} deactivated`),
  setStatus("Activate", <Checkmark20Regular />, ["Inactive"], "Active", (r) => `${String(r[nameKey])} activated`),
];
const positive = (key: string, label: string) => (v: Record<string, Val>) => (num(v[key]) > 0 ? null : `${label} must be greater than zero.`);
const teamOpts = (d: Data): Opt[] => rows(d, "team").filter((t) => t.status === "Active").map((t) => ({ v: String(t.name), l: String(t.name) }));
const accountOpts = (d: Data): Opt[] => rows(d, "accounts").map((a) => ({ v: a.id, l: String(a.name) }));
const oppOpts = (d: Data): Opt[] => rows(d, "opportunities").filter(isOpenOpp).map((o) => ({ v: o.id, l: `${String(o.name)}` }));
const productOpts = (d: Data): Opt[] => rows(d, "products").filter((p) => p.status === "Active").map((p) => ({ v: p.id, l: `${String(p.name)} (${money(num(p.price))})` }));
const draftAct = (who: (r: Row, d: Data) => { name: string; company: string; topic: string }): Act => ({
  label: "Draft email (AI)",
  icon: <Sparkle20Regular />,
  show: () => true,
  run: (r, d) => {
    const w = who(r, d);
    return { draft: draftEmail(w.name, w.company, w.topic, "Your sales team"), msg: "Draft ready" };
  },
});
const quoteTotal = (r: Row, d: Data) => num(rows(d, "products").find((p) => p.id === r.product)?.price) * num(r.qty) * (1 - num(r.discount) / 100);
const quoteState = (r: Row) => (r.status === "Sent" && String(r.valid) < off(0) ? "Expired" : String(r.status));

/* ---------- page definitions ---------- */
const ENTS: Entity[] = [
  {
    id: "Activity", src: "activities", title: "Activity", subtitle: "Calls, emails, meetings and tasks for you and your team.", noun: "activity", page: "grid", prefix: "AT-", editable: ["Open"], initial: { status: "Open" },
    cols: [
      { key: "id", label: "Activity", kind: "mono" }, { key: "type", label: "Type" }, { key: "subject", label: "Subject" },
      { key: "account", label: "Regarding", get: (r, d) => nameOf(d, "accounts", r.account) }, { key: "date", label: "Due", kind: "date" }, { key: "owner", label: "Owner" },
      { key: "state", label: "Status", kind: "status", get: (r) => (r.status === "Open" && String(r.date) < off(0) ? "Overdue" : String(r.status)) },
    ],
    fields: [
      { key: "type", label: "Type", kind: "select", options: opts("Call", "Email", "Meeting", "Task"), required: true },
      { key: "date", label: "Due date", kind: "date", required: true },
      { key: "subject", label: "Subject", kind: "text", required: true, full: true },
      { key: "account", label: "Regarding account", kind: "select", options: accountOpts, required: true },
      { key: "owner", label: "Owner", kind: "select", options: teamOpts, required: true },
    ],
    filterKey: "state",
    summary: (list) => {
      const open = list.filter((r) => r.status === "Open");
      return [
        { label: "Due today", value: String(open.filter((r) => r.date === off(0)).length), note: "Open activities" },
        { label: "Overdue", value: String(open.filter((r) => String(r.date) < off(0)).length), note: "Need attention" },
        { label: "Open", value: String(open.length), note: "In total" },
        { label: "Completed (7 days)", value: String(list.filter((r) => r.status === "Completed" && String(r.date) >= off(-7)).length), note: "Recent wins" },
      ];
    },
    acts: [
      setStatus("Complete", <Checkmark20Regular />, ["Open"], "Completed", (r) => `${String(r.subject)} completed`),
      {
        label: "Reschedule", icon: <CalendarClock20Regular />, show: (r) => r.status === "Open",
        prompt: { title: "Reschedule activity", submit: "Reschedule", fields: [{ key: "date", label: "New due date", kind: "date", required: true }], init: (r) => ({ date: String(r.date) < off(0) ? off(1) : String(r.date) }) },
        run: (r, _d, v) => ({ patch: { date: String(v.date) }, msg: `${String(r.subject)} moved to ${fdate(String(v.date))}` }),
      },
      setStatus("Cancel", <Dismiss20Regular />, ["Open"], "Cancelled", (r) => `${String(r.subject)} cancelled`),
    ],
  },
  {
    id: "Accounts", src: "accounts", title: "Accounts", subtitle: "Companies you sell to, with their open deals and health.", noun: "account", page: "grid", prefix: "AC-", initial: { status: "Prospect" },
    cols: [
      { key: "id", label: "Account", kind: "mono" }, { key: "name", label: "Name" }, { key: "industry", label: "Industry" }, { key: "city", label: "City" },
      { key: "employees", label: "Employees", kind: "num", right: true }, { key: "revenue", label: "Annual revenue", kind: "money", right: true },
      { key: "open", label: "Open deals", kind: "money", right: true, get: (r, d) => sum(rows(d, "opportunities").filter((o) => o.account === r.id && isOpenOpp(o)), (o) => num(o.amount)) },
      { key: "owner", label: "Owner" }, { key: "status", label: "Status", kind: "status" },
    ],
    fields: [
      { key: "name", label: "Company name", kind: "text", required: true, full: true },
      { key: "industry", label: "Industry", kind: "select", options: opts("Banking", "Fintech", "Insurance", "Oil and gas", "Telecoms", "Consulting", "Retail", "Healthcare", "Media"), required: true },
      { key: "city", label: "City", kind: "text", required: true },
      { key: "employees", label: "Employees", kind: "number" }, { key: "revenue", label: "Annual revenue", kind: "number" },
      { key: "owner", label: "Owner", kind: "select", options: teamOpts, required: true },
      { key: "status", label: "Status", kind: "select", options: opts("Prospect", "Customer", "Churned"), required: true },
    ],
    canDelete: (r, d) => (rows(d, "opportunities").some((o) => o.account === r.id) || rows(d, "contacts").some((c) => c.account === r.id) ? "This account has contacts or deals. Set it to Churned instead." : null),
    summary: (list) => [
      { label: "Accounts", value: String(list.length) }, { label: "Customers", value: String(list.filter((r) => r.status === "Customer").length) },
      { label: "Prospects", value: String(list.filter((r) => r.status === "Prospect").length) }, { label: "Churned", value: String(list.filter((r) => r.status === "Churned").length) },
    ],
    detail: (r, d) => (
      <div className={styles.crAi}>
        <div className={styles.crAiHead}><Sparkle20Regular /> AI summary</div>
        <p>{accountSummary(r, d)}</p>
      </div>
    ),
    acts: [draftAct((r, d) => ({ name: String(rows(d, "contacts").find((c) => c.account === r.id)?.name ?? "there"), company: String(r.name), topic: "your account" }))],
  },
  {
    id: "Linked Contact", src: "contacts", title: "Linked Contact", subtitle: "People at your accounts.", noun: "contact", page: "grid", prefix: "CT-", initial: { status: "Active" },
    cols: [
      { key: "id", label: "Contact", kind: "mono" }, { key: "name", label: "Name" }, { key: "title", label: "Job title" }, { key: "account", label: "Account", get: (r, d) => nameOf(d, "accounts", r.account) },
      { key: "email", label: "Email" }, { key: "phone", label: "Phone" }, { key: "status", label: "Status", kind: "status" },
    ],
    fields: [
      { key: "name", label: "Full name", kind: "text", required: true, full: true }, { key: "title", label: "Job title", kind: "text" },
      { key: "account", label: "Account", kind: "select", options: accountOpts, required: true }, { key: "email", label: "Email", kind: "text" }, { key: "phone", label: "Phone", kind: "text" },
    ],
    acts: [...toggleActs("name"), draftAct((r, d) => ({ name: String(r.name), company: nameOf(d, "accounts", r.account), topic: "your recent enquiry" }))],
  },
  {
    id: "Leads", src: "leads", title: "Leads", subtitle: "Prospects not yet qualified. AI scores each lead from 0 to 100.", noun: "lead", page: "grid", prefix: "LD-", editable: ["New", "Contacted"], initial: { status: "New" },
    cols: [
      { key: "id", label: "Lead", kind: "mono" }, { key: "name", label: "Name" }, { key: "company", label: "Company" }, { key: "source", label: "Source" },
      { key: "budget", label: "Budget", kind: "money", right: true },
      { key: "score", label: "AI score", kind: "bar", get: (r, d) => leadScore(r, d).score },
      { key: "heat", label: "Rating", kind: "status", get: (r, d) => scoreLabel(leadScore(r, d).score) },
      { key: "owner", label: "Owner" }, { key: "status", label: "Status", kind: "status" },
    ],
    fields: [
      { key: "name", label: "Full name", kind: "text", required: true }, { key: "title", label: "Job title", kind: "text" },
      { key: "company", label: "Company", kind: "text", required: true }, { key: "source", label: "Source", kind: "select", options: opts("Referral", "Event", "Website", "Social", "Cold call"), required: true },
      { key: "email", label: "Email", kind: "text" }, { key: "phone", label: "Phone", kind: "text" },
      { key: "budget", label: "Budget", kind: "number" }, { key: "owner", label: "Owner", kind: "select", options: teamOpts, required: true },
      { key: "campaign", label: "Campaign (optional)", kind: "select", options: (d) => rows(d, "campaigns").map((c) => ({ v: c.id, l: String(c.name) })), full: true },
    ],
    summary: (list, d) => {
      const live = list.filter((r) => !["Converted", "Disqualified"].includes(String(r.status)));
      return [
        { label: "Open leads", value: String(live.length) }, { label: "Hot leads", value: String(live.filter((r) => leadScore(r, d).score >= 70).length), note: "Score 70 or more" },
        { label: "Qualified", value: String(list.filter((r) => r.status === "Qualified").length) }, { label: "Converted", value: String(list.filter((r) => r.status === "Converted").length) },
      ];
    },
    detail: (r, d) => {
      const s = leadScore(r, d);
      return (
        <div className={styles.crAi}>
          <div className={styles.crAiHead}><Sparkle20Regular /> Why {s.score}/100</div>
          <ul>{s.why.map((w) => <li key={w}>{w}</li>)}</ul>
        </div>
      );
    },
    acts: [
      setStatus("Mark contacted", <Phone20Regular />, ["New"], "Contacted", (r) => `${String(r.name)} marked contacted`),
      setStatus("Qualify", <Checkmark20Regular />, ["New", "Contacted"], "Qualified", (r) => `${String(r.name)} qualified`),
      {
        label: "Convert", icon: <ArrowRight20Regular />, show: (r) => r.status === "Qualified",
        run: (r, d) => {
          const acc = rows(d, "accounts").find((a) => String(a.name).toLowerCase() === String(r.company).toLowerCase());
          const accId = acc?.id ?? nextId("AC-", rows(d, "accounts"));
          const creates: NonNullable<Res["creates"]> = [
              ...(acc ? [] : [{ ent: "accounts", id: accId, values: { name: String(r.company), industry: "", city: "", employees: 0, revenue: 0, owner: String(r.owner), status: "Prospect" } }]),
              { ent: "contacts", values: { name: String(r.name), title: String(r.title ?? ""), account: accId, email: String(r.email ?? ""), phone: String(r.phone ?? ""), status: "Active" } },
              { ent: "opportunities", values: { name: `${String(r.company)} opportunity`, account: accId, amount: num(r.budget), close: off(45), stage: "Qualify", owner: String(r.owner) } },
          ];
          return { patch: { status: "Converted" }, creates, msg: `${String(r.name)} converted to a contact and opportunity at ${String(r.company)}` };
        },
      },
      setStatus("Disqualify", <Dismiss20Regular />, ["New", "Contacted", "Qualified"], "Disqualified", (r) => `${String(r.name)} disqualified`),
      draftAct((r) => ({ name: String(r.name), company: String(r.company), topic: "how we can help" })),
    ],
  },
  {
    id: "Opportunity", src: "opportunities", title: "Opportunity", subtitle: "Deals in progress. Win probability follows the stage.", noun: "opportunity", page: "grid", prefix: "OP-", editable: ["Qualify", "Develop", "Propose", "Negotiate"], initial: { stage: "Qualify" },
    cols: [
      { key: "id", label: "Deal", kind: "mono" }, { key: "name", label: "Name" }, { key: "account", label: "Account", get: (r, d) => nameOf(d, "accounts", r.account) },
      { key: "amount", label: "Amount", kind: "money", right: true }, { key: "stage", label: "Stage", kind: "status" },
      { key: "prob", label: "Probability", kind: "pct", right: true, get: (r) => prob(r) },
      { key: "weighted", label: "Weighted", kind: "money", right: true, get: (r) => (num(r.amount) * prob(r)) / 100 },
      { key: "close", label: "Close date", kind: "date" }, { key: "health", label: "AI health", kind: "status", get: (r, d) => health(r, d) }, { key: "owner", label: "Owner" },
    ],
    fields: [
      { key: "name", label: "Deal name", kind: "text", required: true, full: true }, { key: "account", label: "Account", kind: "select", options: accountOpts, required: true },
      { key: "amount", label: "Amount", kind: "number", required: true }, { key: "close", label: "Expected close", kind: "date", required: true },
      { key: "owner", label: "Owner", kind: "select", options: teamOpts, required: true },
    ],
    validate: positive("amount", "Amount"),
    filterKey: "stage", filterLabel: "All stages",
    summary: (list) => {
      const open = list.filter(isOpenOpp);
      return [
        { label: "Open pipeline", value: money(sum(open, (r) => num(r.amount))), note: `${open.length} deals` },
        { label: "Weighted", value: money(sum(open, (r) => (num(r.amount) * prob(r)) / 100)), note: "Amount × probability" },
        { label: "Won", value: money(sum(list.filter((r) => r.stage === "Won"), (r) => num(r.amount))), note: `${list.filter((r) => r.stage === "Won").length} deals` },
        { label: "Lost", value: String(list.filter((r) => r.stage === "Lost").length), note: "Deals" },
      ];
    },
    detail: (r, d) => (
      <div className={styles.crAi}>
        <div className={styles.crAiHead}><Sparkle20Regular /> Next best action</div>
        <p>{nextAction(r, d)}</p>
      </div>
    ),
    acts: [
      {
        label: "Advance stage", icon: <ArrowRight20Regular />, show: (r) => ["Qualify", "Develop", "Propose"].includes(String(r.stage)),
        run: (r) => { const to = STAGES[STAGES.indexOf(String(r.stage)) + 1] ?? "Develop"; return { patch: { stage: to }, msg: `${String(r.name)} moved to ${to}` }; },
      },
      { label: "Mark won", icon: <Checkmark20Regular />, show: isOpenOpp, run: (r) => ({ patch: { stage: "Won" }, msg: `${String(r.name)} won. ${money(num(r.amount))} booked` }) },
      {
        label: "Mark lost", icon: <Dismiss20Regular />, show: isOpenOpp,
        prompt: { title: "Mark deal as lost", submit: "Mark lost", fields: [{ key: "reason", label: "Reason", kind: "select", options: opts("Price", "Competitor", "No budget", "No decision", "Timing"), required: true }], init: () => ({ reason: "" }) },
        run: (r, _d, v) => ({ patch: { stage: "Lost", lostReason: String(v.reason) }, msg: `${String(r.name)} marked lost: ${String(v.reason)}` }),
      },
      draftAct((r, d) => ({ name: String(rows(d, "contacts").find((c) => c.account === r.account)?.name ?? "there"), company: nameOf(d, "accounts", r.account), topic: String(r.name) })),
    ],
  },
  { id: "Pipeline", src: "opportunities", title: "Pipeline", subtitle: "Drag a deal between stages, or use the arrows.", noun: "", page: "board", cols: [], fields: [], acts: [] },
  { id: "Forecast", src: "opportunities", title: "Forecast", subtitle: "Weighted revenue forecast with AI insights.", noun: "", page: "forecast", cols: [], fields: [], acts: [] },
  { id: "AI assistant", src: "chat", title: "AI assistant", subtitle: "Ask about your pipeline, leads, accounts and support cases.", noun: "", page: "ai", cols: [], fields: [], acts: [] },
  {
    id: "Compatitors", src: "competitors", title: "Compatitors", subtitle: "Who you compete with and how to beat them.", noun: "competitor", page: "grid", prefix: "CP-",
    cols: [
      { key: "name", label: "Name" }, { key: "pricing", label: "Pricing" }, { key: "share", label: "Market share", kind: "pct", right: true },
      { key: "threat", label: "Threat", kind: "status" }, { key: "strengths", label: "Strengths" }, { key: "weaknesses", label: "Weaknesses" },
      { key: "lost", label: "Deals lost to them", kind: "num", right: true, get: (r, d) => rows(d, "opportunities").filter((o) => o.lostReason === "Competitor" && o.competitor === r.id).length },
    ],
    fields: [
      { key: "name", label: "Name", kind: "text", required: true }, { key: "website", label: "Website", kind: "text" },
      { key: "pricing", label: "Pricing", kind: "select", options: opts("Premium", "Mid-market", "Budget"), required: true }, { key: "share", label: "Market share (%)", kind: "number" },
      { key: "threat", label: "Threat level", kind: "select", options: opts("High", "Medium", "Low"), required: true },
      { key: "strengths", label: "Strengths", kind: "text", full: true }, { key: "weaknesses", label: "Weaknesses", kind: "text", full: true },
    ],
    filterKey: "threat", filterLabel: "All threat levels", acts: [],
  },
  {
    id: "Quotes", src: "quotes", title: "Quotes", subtitle: "Proposals sent to customers. Accepting a quote creates the order and wins the deal.", noun: "quote", page: "grid", prefix: "QT-", editable: ["Draft"], initial: { status: "Draft" },
    cols: [
      { key: "id", label: "Quote", kind: "mono" }, { key: "opp", label: "Opportunity", get: (r, d) => nameOf(d, "opportunities", r.opp) },
      { key: "account", label: "Account", get: (r, d) => nameOf(d, "accounts", rows(d, "opportunities").find((o) => o.id === r.opp)?.account) },
      { key: "product", label: "Product", get: (r, d) => nameOf(d, "products", r.product) }, { key: "qty", label: "Qty", kind: "num", right: true },
      { key: "discount", label: "Discount", kind: "pct", right: true }, { key: "total", label: "Total", kind: "money", right: true, get: (r, d) => quoteTotal(r, d) },
      { key: "valid", label: "Valid until", kind: "date" }, { key: "state", label: "Status", kind: "status", get: (r) => quoteState(r) },
    ],
    fields: [
      { key: "opp", label: "Opportunity", kind: "select", options: oppOpts, required: true, full: true }, { key: "product", label: "Product", kind: "select", options: productOpts, required: true, full: true },
      { key: "qty", label: "Quantity", kind: "number", required: true }, { key: "discount", label: "Discount (%)", kind: "number" }, { key: "valid", label: "Valid until", kind: "date", required: true },
    ],
    validate: (v) => (num(v.qty) <= 0 ? "Quantity must be greater than zero." : num(v.discount) < 0 || num(v.discount) > 100 ? "Discount must be between 0 and 100." : null),
    filterKey: "state",
    summary: (list, d) => [
      { label: "Quoted", value: money(sum(list.filter((r) => ["Sent", "Draft"].includes(String(r.status))), (r) => quoteTotal(r, d))), note: "Draft and sent" },
      { label: "Accepted", value: money(sum(list.filter((r) => r.status === "Accepted"), (r) => quoteTotal(r, d))) }, { label: "Awaiting reply", value: String(list.filter((r) => r.status === "Sent").length) },
    ],
    acts: [
      setStatus("Send", <Send20Regular />, ["Draft"], "Sent", (r) => `${r.id} sent to the customer`),
      {
        label: "Accept", icon: <Checkmark20Regular />, show: (r) => r.status === "Sent",
        run: (r, d) => {
          const opp = rows(d, "opportunities").find((o) => o.id === r.opp);
          const total = quoteTotal(r, d);
          return {
            patch: { status: "Accepted" },
            other: opp ? [{ ent: "opportunities", id: opp.id, patch: { stage: "Won", amount: total } }] : [],
            creates: [{ ent: "orders", values: { quote: r.id, account: String(opp?.account ?? ""), date: off(0), product: String(r.product), qty: num(r.qty), total, status: "Active" } }],
            msg: `${r.id} accepted. Order created and ${String(opp?.name ?? "the deal")} won`,
          };
        },
      },
      setStatus("Reject", <Dismiss20Regular />, ["Sent"], "Rejected", (r) => `${r.id} rejected`),
    ],
  },
  {
    id: "Orders", src: "orders", title: "Orders", subtitle: "Sales orders created from accepted quotes.", noun: "order", page: "grid", readOnly: true,
    cols: [
      { key: "id", label: "Order", kind: "mono" }, { key: "quote", label: "Quote", kind: "mono" }, { key: "account", label: "Account", get: (r, d) => nameOf(d, "accounts", r.account) },
      { key: "product", label: "Product", get: (r, d) => nameOf(d, "products", r.product) }, { key: "qty", label: "Qty", kind: "num", right: true },
      { key: "date", label: "Date", kind: "date" }, { key: "total", label: "Total", kind: "money", right: true }, { key: "status", label: "Status", kind: "status" },
    ],
    fields: [],
    summary: (list) => [
      { label: "Active orders", value: String(list.filter((r) => r.status === "Active").length) },
      { label: "Active value", value: money(sum(list.filter((r) => r.status === "Active"), (r) => num(r.total))) },
      { label: "Fulfilled", value: money(sum(list.filter((r) => r.status === "Fulfilled"), (r) => num(r.total))) },
    ],
    acts: [
      {
        label: "Fulfil and invoice", icon: <Checkmark20Regular />, show: (r) => r.status === "Active",
        run: (r) => ({ patch: { status: "Fulfilled" }, creates: [{ ent: "invoices", values: { order: r.id, account: String(r.account), date: off(0), due: off(30), total: num(r.total), status: "Unpaid" } }], msg: `${r.id} fulfilled and invoice raised` }),
      },
      setStatus("Cancel", <Dismiss20Regular />, ["Active"], "Cancelled", (r) => `${r.id} cancelled`),
    ],
  },
  {
    id: "Linked Invoices", src: "invoices", title: "Linked Invoices", subtitle: "Invoices raised from fulfilled orders.", noun: "invoice", page: "grid", readOnly: true,
    cols: [
      { key: "id", label: "Invoice", kind: "mono" }, { key: "order", label: "Order", kind: "mono" }, { key: "account", label: "Account", get: (r, d) => nameOf(d, "accounts", r.account) },
      { key: "date", label: "Date", kind: "date" }, { key: "due", label: "Due", kind: "date" }, { key: "total", label: "Amount", kind: "money", right: true },
      { key: "state", label: "Status", kind: "status", get: (r) => (r.status === "Unpaid" && String(r.due) < off(0) ? "Overdue" : String(r.status)) },
    ],
    fields: [], filterKey: "state",
    summary: (list) => [
      { label: "Outstanding", value: money(sum(list.filter((r) => r.status === "Unpaid"), (r) => num(r.total))) },
      { label: "Overdue", value: money(sum(list.filter((r) => r.status === "Unpaid" && String(r.due) < off(0)), (r) => num(r.total))) },
      { label: "Paid", value: money(sum(list.filter((r) => r.status === "Paid"), (r) => num(r.total))) },
    ],
    acts: [setStatus("Mark paid", <Checkmark20Regular />, ["Unpaid"], "Paid", (r) => `${r.id} marked paid`)],
  },
  {
    id: "Products", src: "products", title: "Products", subtitle: "Your price list.", noun: "product", page: "grid", prefix: "PR-", initial: { status: "Active" },
    cols: [
      { key: "sku", label: "SKU", kind: "mono" }, { key: "name", label: "Name" }, { key: "category", label: "Category" }, { key: "price", label: "Price", kind: "money", right: true }, { key: "status", label: "Status", kind: "status" },
    ],
    fields: [
      { key: "sku", label: "SKU", kind: "text", required: true }, { key: "name", label: "Name", kind: "text", required: true },
      { key: "category", label: "Category", kind: "select", options: opts("Software", "Services", "Support", "Training"), required: true }, { key: "price", label: "Price", kind: "number", required: true },
    ],
    validate: positive("price", "Price"),
    canDelete: (r, d) => (rows(d, "quotes").some((q) => q.product === r.id) ? "This product is on a quote. Deactivate it instead." : null),
    acts: toggleActs("name"),
  },
  {
    id: "Campaigns", src: "campaigns", title: "Campaigns", subtitle: "Marketing campaigns with leads generated and return on spend.", noun: "campaign", page: "grid", prefix: "CM-", editable: ["Draft"], initial: { status: "Draft", spent: 0, revenue: 0 },
    cols: [
      { key: "id", label: "Campaign", kind: "mono" }, { key: "name", label: "Name" }, { key: "type", label: "Type" }, { key: "start", label: "Start", kind: "date" }, { key: "end", label: "End", kind: "date" },
      { key: "budget", label: "Budget", kind: "money", right: true }, { key: "spent", label: "Spent", kind: "money", right: true },
      { key: "leads", label: "Leads", kind: "num", right: true, get: (r, d) => rows(d, "leads").filter((l) => l.campaign === r.id).length },
      { key: "roi", label: "ROI", kind: "pct", right: true, get: (r) => (num(r.spent) ? Math.round(((num(r.revenue) - num(r.spent)) / num(r.spent)) * 100) : 0) },
      { key: "status", label: "Status", kind: "status" },
    ],
    fields: [
      { key: "name", label: "Name", kind: "text", required: true, full: true }, { key: "type", label: "Type", kind: "select", options: opts("Email", "Event", "Webinar", "Social"), required: true },
      { key: "budget", label: "Budget", kind: "number", required: true }, { key: "start", label: "Start date", kind: "date", required: true }, { key: "end", label: "End date", kind: "date", required: true },
    ],
    validate: (v) => (num(v.budget) <= 0 ? "Budget must be greater than zero." : String(v.end) < String(v.start) ? "End date can't be before the start date." : null),
    summary: (list, d) => [
      { label: "Active", value: String(list.filter((r) => r.status === "Active").length) }, { label: "Budget", value: money(sum(list, (r) => num(r.budget))) },
      { label: "Spent", value: money(sum(list, (r) => num(r.spent))) }, { label: "Leads generated", value: String(rows(d, "leads").filter((l) => l.campaign).length) },
    ],
    acts: [
      setStatus("Launch", <Send20Regular />, ["Draft"], "Active", (r) => `${String(r.name)} launched`),
      setStatus("Complete", <Checkmark20Regular />, ["Active"], "Completed", (r) => `${String(r.name)} completed`),
    ],
  },
  {
    id: "Marketing lists", src: "lists", title: "Marketing lists", subtitle: "Audiences for campaigns.", noun: "list", page: "grid", prefix: "ML-", initial: { status: "Active", members: 0 },
    cols: [{ key: "id", label: "List", kind: "mono" }, { key: "name", label: "Name" }, { key: "type", label: "Type" }, { key: "purpose", label: "Purpose" }, { key: "members", label: "Members", kind: "num", right: true }, { key: "status", label: "Status", kind: "status" }],
    fields: [
      { key: "name", label: "Name", kind: "text", required: true, full: true }, { key: "type", label: "Type", kind: "select", options: opts("Static", "Dynamic"), required: true },
      { key: "purpose", label: "Purpose", kind: "select", options: opts("Newsletter", "Event invite", "Product launch", "Re-engagement"), required: true }, { key: "members", label: "Members", kind: "number" },
    ],
    acts: toggleActs("name"),
  },
  {
    id: "Cases", src: "cases", title: "Cases", subtitle: "Customer support tickets with SLA tracking.", noun: "case", page: "grid", prefix: "CS-", editable: ["Open"], initial: { status: "Open" },
    cols: [
      { key: "id", label: "Case", kind: "mono" }, { key: "title", label: "Title" }, { key: "account", label: "Account", get: (r, d) => nameOf(d, "accounts", r.account) },
      { key: "priority", label: "Priority", kind: "status" }, { key: "channel", label: "Channel" }, { key: "owner", label: "Owner" }, { key: "due", label: "SLA due", kind: "date" },
      { key: "state", label: "Status", kind: "status", get: (r) => (["Open", "In progress", "Escalated"].includes(String(r.status)) && String(r.due) < off(0) ? "Breached" : String(r.status)) },
    ],
    fields: [
      { key: "title", label: "Title", kind: "text", required: true, full: true }, { key: "account", label: "Account", kind: "select", options: accountOpts, required: true },
      { key: "priority", label: "Priority", kind: "select", options: opts("High", "Medium", "Low"), required: true }, { key: "channel", label: "Channel", kind: "select", options: opts("Email", "Phone", "Chat", "Web"), required: true },
      { key: "owner", label: "Owner", kind: "select", options: teamOpts, required: true }, { key: "due", label: "SLA due", kind: "date", required: true },
    ],
    filterKey: "state",
    summary: (list) => {
      const live = list.filter((r) => ["Open", "In progress", "Escalated"].includes(String(r.status)));
      return [
        { label: "Open cases", value: String(live.length) }, { label: "Past SLA", value: String(live.filter((r) => String(r.due) < off(0)).length), note: "Need attention" },
        { label: "Escalated", value: String(list.filter((r) => r.status === "Escalated").length) }, { label: "Resolved", value: String(list.filter((r) => r.status === "Resolved").length) },
      ];
    },
    acts: [
      setStatus("Start work", <ArrowRight20Regular />, ["Open"], "In progress", (r) => `${r.id} is in progress`),
      setStatus("Escalate", <Alert20Regular />, ["Open", "In progress"], "Escalated", (r) => `${r.id} escalated`),
      {
        label: "Resolve", icon: <Checkmark20Regular />, show: (r) => ["Open", "In progress", "Escalated"].includes(String(r.status)),
        prompt: { title: "Resolve case", submit: "Resolve", fields: [{ key: "note", label: "Resolution", kind: "text", required: true, full: true }], init: () => ({ note: "" }) },
        run: (r, _d, v) => ({ patch: { status: "Resolved", resolution: String(v.note) }, msg: `${r.id} resolved` }),
      },
      setStatus("Reopen", <ArrowUndo20Regular />, ["Resolved"], "Open", (r) => `${r.id} reopened`),
    ],
  },
  {
    id: "Knowledge base", src: "articles", title: "Knowledge base", subtitle: "Articles for agents and customers.", noun: "article", page: "grid", prefix: "KB-", initial: { status: "Draft", views: 0 },
    cols: [{ key: "id", label: "Article", kind: "mono" }, { key: "title", label: "Title" }, { key: "category", label: "Category" }, { key: "author", label: "Author" }, { key: "views", label: "Views", kind: "num", right: true }, { key: "status", label: "Status", kind: "status" }],
    fields: [
      { key: "title", label: "Title", kind: "text", required: true, full: true }, { key: "category", label: "Category", kind: "select", options: opts("Getting started", "Billing", "Integrations", "Troubleshooting", "Policies"), required: true },
      { key: "author", label: "Author", kind: "select", options: teamOpts, required: true },
    ],
    acts: [setStatus("Publish", <Checkmark20Regular />, ["Draft"], "Published", (r) => `${String(r.title)} published`), setStatus("Unpublish", <Dismiss20Regular />, ["Published"], "Draft", (r) => `${String(r.title)} unpublished`)],
  },
  {
    id: "Sales team", src: "team", title: "Sales team", subtitle: "People, quotas and attainment from won deals.", noun: "team member", page: "grid", prefix: "TM-", initial: { status: "Active" },
    cols: [
      { key: "id", label: "Member", kind: "mono" }, { key: "name", label: "Name" }, { key: "role", label: "Role" }, { key: "region", label: "Region" }, { key: "quota", label: "Annual quota", kind: "money", right: true },
      { key: "won", label: "Won", kind: "money", right: true, get: (r, d) => sum(rows(d, "opportunities").filter((o) => o.stage === "Won" && o.owner === r.name), (o) => num(o.amount)) },
      { key: "attain", label: "Attainment", kind: "bar", get: (r, d) => teamAttain(r, d) }, { key: "status", label: "Status", kind: "status" },
    ],
    fields: [
      { key: "name", label: "Full name", kind: "text", required: true, full: true }, { key: "role", label: "Role", kind: "select", options: opts("Sales rep", "Account manager", "Sales manager", "Sales director", "Support agent"), required: true },
      { key: "region", label: "Region", kind: "select", options: opts("Lagos", "Abuja", "Port Harcourt", "Kano", "Enugu"), required: true }, { key: "quota", label: "Annual quota", kind: "number", required: true },
    ],
    canDelete: (r, d) => (rows(d, "opportunities").some((o) => o.owner === r.name) ? "This person owns deals. Deactivate them instead." : null),
    acts: toggleActs("name"),
  },
  {
    id: "Activity log", src: "audit", title: "Activity log", subtitle: "Every change made in CRM, newest first.", noun: "entry", page: "grid", readOnly: true,
    cols: [{ key: "when", label: "When" }, { key: "user", label: "User" }, { key: "entity", label: "Page" }, { key: "record", label: "Record", kind: "mono" }, { key: "action", label: "Action" }, { key: "detail", label: "Detail" }],
    fields: [], acts: [],
  },
];

/* ---------- form dialog ---------- */
const RecordDialog: React.FC<{ title: string; submit: string; fields: Fld[]; data: Data; initial: Record<string, Val>; onClose: () => void; onSave: (v: Record<string, Val>) => string | null | Promise<string | null> }> = ({ title, submit, fields, data, initial, onClose, onSave }) => {
  const [v, setV] = useState<Record<string, string>>(() => {
    const o: Record<string, string> = {};
    fields.forEach((f) => {
      const cur = initial[f.key];
      o[f.key] = cur !== undefined ? String(cur) : f.kind === "date" ? off(0) : "";
    });
    return o;
  });
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const go = () => {
    if (busy) return;
    const missing = fields.find((f) => f.required && !(v[f.key] ?? "").trim());
    if (missing) return setErr(`${missing.label} is required.`);
    const out: Record<string, Val> = {};
    fields.forEach((f) => {
      const raw = (v[f.key] ?? "").trim();
      out[f.key] = f.kind === "number" ? Number(raw) || 0 : raw;
    });
    const bad = onSave(out);
    if (bad && typeof bad !== "string") {
      setBusy(true);
      bad.then((m) => { setBusy(false); if (m) setErr(m); }).catch(() => { setBusy(false); setErr("Something went wrong."); });
    } else if (bad) setErr(bad);
  };
  return (
    <Dialog open onOpenChange={(_, dd) => !dd.open && onClose()}>
      <DialogSurface className={styles.crDialog}>
        <DialogBody>
          <DialogTitle action={<DialogTrigger action="close"><Button size="small" appearance="subtle" aria-label="Close" icon={<Dismiss24Regular />} /></DialogTrigger>}>{title}</DialogTitle>
          <DialogContent>
            <div className={styles.crForm}>
              {fields.map((f) => {
                const options = typeof f.options === "function" ? f.options(data) : f.options ?? [];
                return (
                  <div key={f.key} className={f.full ? styles.crFormFull : undefined}>
                    <Field label={f.label} required={!!f.required}>
                      {f.kind === "select" ? (
                        <Select value={v[f.key] ?? ""} onChange={(_, dd) => setV({ ...v, [f.key]: dd.value })}>
                          <option value="">Select…</option>
                          {options.map((o) => <option key={o.v} value={o.v}>{o.l}</option>)}
                        </Select>
                      ) : (
                        <Input type={f.kind === "number" ? "number" : f.kind === "date" ? "date" : "text"} value={v[f.key] ?? ""} onChange={(_, dd) => setV({ ...v, [f.key]: dd.value })} />
                      )}
                    </Field>
                  </div>
                );
              })}
            </div>
            {err && <div className={styles.crErr} role="alert">{err}</div>}
          </DialogContent>
          <DialogActions>
            <DialogTrigger action="close"><Button size="small">Cancel</Button></DialogTrigger>
            <Button size="small" className={styles.crPrimary} appearance="primary" disabled={busy} onClick={go}>{busy ? "Working…" : submit}</Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
};

/* ---------- backend wiring ----------
   Screens in LIVE_SRCS read and write through the CRM services. The others have no safe field mapping yet and open in "All records". */
interface LiveCtx {
  crm: CrmClient;
  team: Row[];
  put: (src: string, row: Row) => void;
  drop: (src: string, id: string) => void;
  reload: (srcs?: string[]) => Promise<void>;
}
const LIVE_SRCS = ["accounts", "contacts", "leads", "opportunities", "activities", "cases", "competitors", "team"];
/** screens whose sample fields do not match the services (quote lines, prices...) -> the matching screen in All records */
const SERVER_ONLY: Record<string, string> = {
  quotes: "commerce.quotes", orders: "commerce.orders", invoices: "commerce.invoices", products: "commerce.products",
  campaigns: "marketing.campaigns", lists: "marketing.lists", articles: "service.articles", audit: "admin.audit-log",
};
interface BackendAct { path: string; body?: (v: Record<string, Val>) => unknown; reload?: boolean }
/** screen action label -> the service's action endpoint (POST <screen path>/<id>/<path>) */
const BACKEND_ACTS: Record<string, BackendAct> = {
  "activities:Complete": { path: "complete" },
  "activities:Reschedule": { path: "reschedule", body: (v) => ({ due: String(v.date) }) },
  "activities:Cancel": { path: "cancel" },
  "leads:Mark contacted": { path: "contacted" },
  "leads:Qualify": { path: "qualify" },
  "leads:Convert": { path: "convert", body: () => ({ createOpportunity: true }), reload: true },
  "leads:Disqualify": { path: "disqualify", body: (v) => ({ reason: String(v.reason) }) },
  "opportunities:Advance stage": { path: "advance" },
  "opportunities:Mark won": { path: "win" },
  "opportunities:Mark lost": { path: "lose", body: (v) => ({ reason: String(v.reason) }) },
  "cases:Start work": { path: "start" },
  "cases:Escalate": { path: "escalate" },
  "cases:Resolve": { path: "resolve", body: (v) => ({ resolution: String(v.note) }) },
  "cases:Reopen": { path: "reopen" },
};
const DISQUALIFY_PROMPT = { title: "Disqualify lead", submit: "Disqualify", fields: [{ key: "reason", label: "Reason", kind: "text" as const, required: true, full: true }], init: () => ({ reason: "" }) };
/** HTTP status carried by an API error, read without instanceof so it works whichever copy of the error class is loaded. 0 = not an API error. */
const errStatus = (e: unknown): number => (typeof e === "object" && e !== null && typeof (e as { status?: unknown }).status === "number" ? (e as { status: number }).status : 0);
const apiMsg = (e: unknown): string => {
  const status = errStatus(e);
  const o = (typeof e === "object" && e !== null ? e : {}) as { message?: unknown; fields?: unknown };
  const msg = typeof o.message === "string" ? o.message : "Something went wrong.";
  if (status === 403) return "You don't have permission to do that.";
  if (status === 401) return "The CRM service did not accept your sign-in. Sign in again.";
  if (status === 409 || status === 412) return "Someone else changed this record. It was reloaded; try again.";
  const f = status && o.fields && typeof o.fields === "object" ? Object.values(o.fields as Record<string, unknown>).map((x) => (Array.isArray(x) ? x.join(", ") : String(x))).join(" ") : "";
  return f ? `${msg} ${f}` : msg;
};
const upsertRow = (d: Data, src: string, row: Row): Data => {
  const list = rows(d, src);
  return { ...d, [src]: list.some((r) => r.id === row.id) ? list.map((r) => (r.id === row.id ? row : r)) : [row, ...list] };
};
/** The Owner select holds a name; the service wants the person's login id. "Assign automatically" (empty) sends nothing, so the assignment rules run. */
const withOwner = (values: Record<string, Val>, team: Row[], current: Row | null): Record<string, Val> => {
  const out: Record<string, Val> = { ...values };
  const name = String(out.owner ?? "");
  delete out.owner;
  if (name && name !== String(current?.owner ?? "")) {
    const t = team.find((x) => String(x.name) === name && x.userId);
    if (t) out.ownerId = String(t.userId);
  }
  return out;
};
const liveFields = (fs: Fld[]): Fld[] =>
  fs.map((f) => (f.key === "owner"
    ? { ...f, required: false, options: (d: Data) => [{ v: "", l: "Assign automatically" }, ...(typeof f.options === "function" ? f.options(d) : f.options ?? [])] }
    : f));
const haveToken = (): boolean => { try { return !!localStorage.getItem("authToken"); } catch { return false; } };

interface PageProps {
  data: Data;
  setData: React.Dispatch<React.SetStateAction<Data>>;
  say: (msg: string, err?: boolean) => void;
  live?: LiveCtx;
  openExplorer?: (key: string) => void;
}
const val = (c: Col, r: Row, d: Data): Val => (c.get ? c.get(r, d) : c.key === "id" && r.number ? r.number : r[c.key] ?? "");
/** What people read as the record's id: the backend number (AC-0001) when there is one, else the id. */
const tag = (r: Row): string => String(r.number || r.id);
const show = (c: Col, v: Val): string => {
  switch (c.kind) {
    case "money": return money(num(v));
    case "date": return v ? fdate(String(v)) : "";
    case "pct": case "bar": return `${num(v)}%`;
    case "num": return num(v).toLocaleString("en-NG");
    default: return String(v);
  }
};
const Tiles: React.FC<{ tiles: Tile[] }> = ({ tiles }) => (
  <div className={styles.crTiles}>
    {tiles.map((t) => (
      <div key={t.label} className={styles.crTile}>
        <span className={styles.crTileLabel}>{t.label}</span>
        <span className={styles.crTileValue}>{t.value}</span>
        {t.note ? <span className={styles.crTileNote}>{t.note}</span> : null}
      </div>
    ))}
  </div>
);
const UseBar: React.FC<{ pct: number; good?: boolean }> = ({ pct }) => (
  <span className={styles.crUse}>
    <span className={styles.crUseTrack}><span className={`${styles.crUseFill} ${pct >= 70 ? styles.crUseHigh : pct >= 40 ? styles.crUseMid : ""}`} style={{ width: `${Math.min(pct, 100)}%` }} /></span>
    <span>{pct}</span>
  </span>
);

/* ---------- record page: related items, notes and files for the selected row ---------- */
interface RelatedDef { title: string; src: string; match: (c: Row, r: Row) => boolean; label: (c: Row) => string; sub: (c: Row) => string }
const sameAccount = (c: Row, r: Row) => !!r.account && c.account === r.account;
const REL: Record<string, RelatedDef[]> = {
  accounts: [
    { title: "Contacts", src: "contacts", match: (c, r) => c.account === r.id, label: (c) => String(c.name), sub: (c) => String(c.title ?? "") },
    { title: "Opportunities", src: "opportunities", match: (c, r) => c.account === r.id, label: (c) => String(c.name), sub: (c) => `${String(c.stage)} · ${money(num(c.amount))}` },
    { title: "Cases", src: "cases", match: (c, r) => c.account === r.id, label: (c) => String(c.title), sub: (c) => String(c.status) },
    { title: "Activities", src: "activities", match: (c, r) => c.account === r.id, label: (c) => String(c.subject), sub: (c) => `${String(c.type)} · ${String(c.status)} · ${fdate(String(c.date))}` },
  ],
  contacts: [
    { title: "Opportunities at this account", src: "opportunities", match: sameAccount, label: (c) => String(c.name), sub: (c) => `${String(c.stage)} · ${money(num(c.amount))}` },
    { title: "Cases at this account", src: "cases", match: sameAccount, label: (c) => String(c.title), sub: (c) => String(c.status) },
    { title: "Activities at this account", src: "activities", match: sameAccount, label: (c) => String(c.subject), sub: (c) => `${String(c.type)} · ${String(c.status)} · ${fdate(String(c.date))}` },
  ],
  leads: [
    { title: "Activities", src: "activities", match: (c, r) => c.lead === r.id, label: (c) => String(c.subject), sub: (c) => `${String(c.type)} · ${String(c.status)} · ${fdate(String(c.date))}` },
  ],
  opportunities: [
    { title: "Contacts at this account", src: "contacts", match: sameAccount, label: (c) => String(c.name), sub: (c) => String(c.title ?? "") },
    { title: "Activities at this account", src: "activities", match: sameAccount, label: (c) => String(c.subject), sub: (c) => `${String(c.type)} · ${String(c.status)} · ${fdate(String(c.date))}` },
    { title: "Cases at this account", src: "cases", match: sameAccount, label: (c) => String(c.title), sub: (c) => String(c.status) },
  ],
  cases: [
    { title: "Contacts at this account", src: "contacts", match: sameAccount, label: (c) => String(c.name), sub: (c) => String(c.title ?? "") },
    { title: "Activities at this account", src: "activities", match: sameAccount, label: (c) => String(c.subject), sub: (c) => `${String(c.type)} · ${String(c.status)} · ${fdate(String(c.date))}` },
  ],
};
/** the field of a Note that points at each kind of record (the notes service links to these four) */
const NOTE_FIELD: Record<string, string> = { accounts: "accountId", contacts: "contactId", leads: "leadId", opportunities: "opportunityId" };
/** the name the file service stores for the record a file belongs to */
const REGARDING: Record<string, string> = { accounts: "Account", contacts: "Contact", leads: "Lead", opportunities: "Opportunity", cases: "Case", activities: "Activity", competitors: "Competitor", team: "TeamMember" };
const MAX_UPLOAD = 10 * 1024 * 1024;
const fileSize = (n: number) => (n >= 1048576 ? `${(n / 1048576).toFixed(1)} MB` : n >= 1024 ? `${Math.round(n / 1024)} KB` : `${n} B`);
type SrvRec = { id: string; [k: string]: unknown };

const RecordExtras: React.FC<{ ent: Entity; rec: Row; data: Data; live: boolean; say: PageProps["say"] }> = ({ ent, rec, data, live, say }) => {
  const be = useCrmBackend();
  const rels = REL[ent.src] ?? [];
  const noteField = NOTE_FIELD[ent.src];
  const regarding = REGARDING[ent.src];
  const server = live && !!be;
  const tabs = [...(rels.length ? ["Related"] : []), ...(noteField ? ["Notes"] : []), ...(regarding ? ["Files"] : [])];
  const [tab, setTab] = useState(tabs[0] ?? "");
  const [notes, setNotes] = useState<SrvRec[] | null>(null);
  const [files, setFiles] = useState<SrvRec[] | null>(null);
  const [err, setErr] = useState("");
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  const loadNotes = useCallback(async () => {
    if (!be || !noteField) return;
    setErr("");
    try {
      const page = await be.api.list("api/activities/notes", { pageSize: 100, filters: { [noteField]: rec.id } });
      setNotes([...(page.items as SrvRec[])].sort((a, b) => String(b.createdAt ?? "").localeCompare(String(a.createdAt ?? ""))));
    } catch (e) { setNotes([]); setErr(apiMsg(e)); }
  }, [be, noteField, rec.id]);
  const loadFiles = useCallback(async () => {
    if (!be || !regarding) return;
    setErr("");
    try {
      const page = await be.api.list("api/integrations/files", { pageSize: 100, filters: { regardingType: regarding, regardingId: rec.id } });
      setFiles([...(page.items as SrvRec[])].sort((a, b) => String(b.createdAt ?? "").localeCompare(String(a.createdAt ?? ""))));
    } catch (e) { setFiles([]); setErr(apiMsg(e)); }
  }, [be, regarding, rec.id]);
  useEffect(() => {
    if (!server) return;
    if (tab === "Notes") void loadNotes();
    if (tab === "Files") void loadFiles();
  }, [tab, server, loadNotes, loadFiles]);

  const addNote = async () => {
    const t = text.trim();
    if (!be || !noteField || !t || busy) return;
    setBusy(true);
    try { await be.api.create("api/activities/notes", { text: t, [noteField]: rec.id }); setText(""); await loadNotes(); say("Note added"); }
    catch (e) { say(apiMsg(e), true); }
    setBusy(false);
  };
  const delNote = async (n: SrvRec) => {
    if (!be || !window.confirm("Delete this note?")) return;
    try { await be.api.remove("api/activities/notes", n.id); await loadNotes(); say("Note deleted"); } catch (e) { say(apiMsg(e), true); }
  };
  const upload = async (f: File | undefined) => {
    if (!be || !regarding || !f) return;
    if (f.size > MAX_UPLOAD) return say(`${f.name} is larger than ${fileSize(MAX_UPLOAD)}. Choose a smaller file.`, true);
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      fd.append("regardingType", regarding);
      fd.append("regardingId", rec.id);
      await be.api.json("POST", "api/integrations/files", { form: fd });
      await loadFiles();
      say(`${f.name} uploaded`);
    } catch (e) { say(apiMsg(e), true); }
    setBusy(false);
    if (fileRef.current) fileRef.current.value = "";
  };
  const delFile = async (f: SrvRec) => {
    if (!be || !window.confirm(`Delete ${String(f.fileName)}?`)) return;
    try { await be.api.json("DELETE", `api/integrations/files/${f.id}`); await loadFiles(); say("File deleted"); } catch (e) { say(apiMsg(e), true); }
  };
  const download = async (f: SrvRec) => {
    if (!be) return;
    try { await be.api.download("GET", `api/integrations/files/${f.id}/download`, undefined, String(f.fileName ?? "file")); } catch (e) { say(apiMsg(e), true); }
  };

  if (!tabs.length) return null;
  const when = (v: unknown) => (v ? new Date(String(v)).toLocaleString("en-GB", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "");
  return (
    <section className={styles.crExtras} aria-label="Related items, notes and files">
      <TabList size="small" selectedValue={tab} onTabSelect={(_, d) => { setErr(""); setTab(String(d.value)); }}>
        {tabs.map((t) => <Tab key={t} value={t}>{t}</Tab>)}
      </TabList>
      {tab === "Related" && (
        <div className={styles.crExBody}>
          {rels.map((def) => {
            const items = rows(data, def.src).filter((c) => def.match(c, rec));
            return (
              <div key={def.title} className={styles.crRel}>
                <div className={styles.crRelHead}>{def.title} <span>{items.length}</span></div>
                {items.length === 0 ? <div className={styles.crExEmpty}>None</div> : items.slice(0, 5).map((c) => (
                  <div key={c.id} className={styles.crRelRow}><span>{def.label(c)}</span><small>{def.sub(c)}</small></div>
                ))}
                {items.length > 5 && <div className={styles.crExEmpty}>and {items.length - 5} more in {cap(def.src)}</div>}
              </div>
            );
          })}
        </div>
      )}
      {tab === "Notes" && (
        <div className={styles.crExBody}>
          {!server ? <div className={styles.crExEmpty}>Notes are saved on the server. Connect to the CRM to add them.</div> : (
            <>
              <Textarea value={text} onChange={(_, d) => setText(d.value)} placeholder="Write a note" maxLength={4000} resize="vertical" />
              <div><Button size="small" appearance="primary" className={styles.crPrimary} disabled={busy || !text.trim()} onClick={() => void addNote()}>Add note</Button></div>
              {err && <div className={styles.crErr} role="alert">{err}</div>}
              {notes === null && !err && <div className={styles.crExEmpty}>Loading…</div>}
              {notes?.length === 0 && !err && <div className={styles.crExEmpty}>No notes yet.</div>}
              {notes?.map((n) => (
                <div key={n.id} className={styles.crNote}>
                  <p>{String(n.text ?? "")}</p>
                  <small>{String(n.createdByName ?? "")} {when(n.createdAt)}</small>
                  <Button size="small" appearance="subtle" icon={<Delete16Regular />} aria-label="Delete note" onClick={() => void delNote(n)} />
                </div>
              ))}
            </>
          )}
        </div>
      )}
      {tab === "Files" && (
        <div className={styles.crExBody}>
          {!server ? <div className={styles.crExEmpty}>Files are stored on the server. Connect to the CRM to attach them.</div> : (
            <>
              <input ref={fileRef} type="file" hidden onChange={(e) => void upload(e.target.files?.[0])} />
              <div><Button size="small" appearance="primary" className={styles.crPrimary} disabled={busy} icon={<Add16Regular />} onClick={() => fileRef.current?.click()}>{busy ? "Working…" : "Attach file"}</Button> <small className={styles.crExEmpty}>Up to {fileSize(MAX_UPLOAD)}</small></div>
              {err && <div className={styles.crErr} role="alert">{err}</div>}
              {files === null && !err && <div className={styles.crExEmpty}>Loading…</div>}
              {files?.length === 0 && !err && <div className={styles.crExEmpty}>No files yet.</div>}
              {files?.map((f) => (
                <div key={f.id} className={styles.crNote}>
                  <p><Link as="button" onClick={() => void download(f)}>{String(f.fileName ?? "file")}</Link></p>
                  <small>{fileSize(num(f.sizeBytes as Val))} · {when(f.createdAt)}</small>
                  <Button size="small" appearance="subtle" icon={<Delete16Regular />} aria-label="Delete file" onClick={() => void delFile(f)} />
                </div>
              ))}
            </>
          )}
        </div>
      )}
    </section>
  );
};

/* ---------- saved views ---------- */
interface SavedView { name: string; q: string; filterV: string; sort: { key: string; dir: 1 | -1 } | null; pageSize: number }
const PAGE_SIZES = [10, 25, 50, 100];
const readViews = (key: string): SavedView[] => {
  try {
    const raw = localStorage.getItem(key);
    const a: unknown = raw ? JSON.parse(raw) : [];
    return Array.isArray(a) ? (a as SavedView[]).filter((v) => v && typeof v.name === "string") : [];
  } catch { return []; }
};

const EntityPage: React.FC<PageProps & { ent: Entity }> = ({ ent, data, setData, say, live }) => {
  const isLive = !!live && LIVE_SRCS.includes(ent.src);
  const [q, setQ] = useState("");
  const [filterV, setFilterV] = useState("All");
  const [sort, setSort] = useState<{ key: string; dir: 1 | -1 } | null>(null);
  const [selId, setSelId] = useState<string | null>(null);
  const [form, setForm] = useState<"new" | "edit" | null>(null);
  const [prompt, setPrompt] = useState<Act | null>(null);
  const [draft, setDraft] = useState<Draft | null>(null);
  const [confirmDel, setConfirmDel] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [checked, setChecked] = useState<Set<string>>(new Set());
  const [bulkBusy, setBulkBusy] = useState(false);
  const [bulkDel, setBulkDel] = useState(false);
  const viewsKey = `crm.views.${ent.id}`;
  const [views, setViews] = useState<SavedView[]>(() => readViews(viewsKey));
  const [activeView, setActiveView] = useState("");
  const [viewName, setViewName] = useState<string | null>(null);

  const list = rows(data, ent.src);
  const filterCol = ent.cols.find((c) => c.key === ent.filterKey) ?? ent.cols.find((c) => c.kind === "status");
  const choices = filterCol ? Array.from(new Set(list.map((r) => String(val(filterCol, r, data))))).filter(Boolean).sort() : [];
  const view = useMemo(() => {
    const needle = q.trim().toLowerCase();
    let out = list.filter((r) => {
      if (filterCol && filterV !== "All" && String(val(filterCol, r, data)) !== filterV) return false;
      return !needle || ent.cols.some((c) => show(c, val(c, r, data)).toLowerCase().includes(needle));
    });
    if (sort) {
      const col = ent.cols.find((c) => c.key === sort.key);
      if (col) {
        const dir = sort.dir;
        out = [...out].sort((a, b) => {
          const x = val(col, a, data);
          const y = val(col, b, data);
          return (typeof x === "number" && typeof y === "number" ? x - y : String(x).localeCompare(String(y))) * dir;
        });
      }
    }
    return out;
  }, [list, q, filterV, sort, ent, filterCol, data]);

  const pages = Math.max(1, Math.ceil(view.length / pageSize));
  const cur = Math.min(page, pages);
  const slice = view.slice((cur - 1) * pageSize, cur * pageSize);
  const from = view.length ? (cur - 1) * pageSize + 1 : 0;
  const to = Math.min(cur * pageSize, view.length);
  const picked = list.filter((r) => checked.has(r.id));
  const onPage = slice.filter((r) => checked.has(r.id)).length;

  const sel = list.find((r) => r.id === selId) ?? null;
  const acts = isLive ? ent.acts.map((a) => (a.label === "Disqualify" && ent.src === "leads" && !a.prompt ? { ...a, prompt: DISQUALIFY_PROMPT } : a)) : ent.acts;
  const formFields = isLive ? liveFields(ent.fields) : ent.fields;
  const canChange = !ent.readOnly && !!sel && (!ent.editable || ent.editable.includes(String(sel.status ?? sel.stage)));
  const tiles = ent.summary ? ent.summary(list, data) : null;

  /** quiet: used by bulk actions, which report once at the end instead of once per record */
  const runAct = (a: Act, values: Record<string, Val>, row: Row | null = sel, quiet = false): string | null | Promise<string | null> => {
    if (!row) return null;
    const res = a.run(row, data, values);
    if (res.err) return res.err;
    if (res.draft) {
      if (quiet) return "A draft can't be made for many records at once.";
      setDraft(res.draft);
      return null;
    }
    if (isLive && live) {
      const be = BACKEND_ACTS[`${ent.src}:${a.label}`];
      // no service action of its own: a plain field change (Activate, Deactivate...) is saved as an edit
      const plain = !be && !!res.patch && !res.other?.length && !res.creates?.length;
      if (!be && !plain) return `"${a.label}" is not connected to the server yet. Use All records.`;
      return (async () => {
        try {
          const upd = be ? await live.crm.action(ent.src, row, be.path, be.body ? be.body(values) : undefined) : await live.crm.update(ent.src, row, res.patch ?? {});
          if (upd.id) live.put(ent.src, upd);
          if (be?.reload) await live.reload();
          else if (!upd.id) await live.reload([ent.src]);
          if (!quiet) say(res.msg);
          return null;
        } catch (e) {
          if (errStatus(e) === 409 || errStatus(e) === 412) void live.reload([ent.src]);
          return apiMsg(e);
        }
      })();
    }
    setData((d) => audit(apply(d, ent.src, row.id, res), ent.title, row.id, a.label, res.msg));
    if (!quiet) say(res.msg);
    return null;
  };
  const save = (values: Record<string, Val>): string | null | Promise<string | null> => {
    const bad = ent.validate ? ent.validate(values, data, form === "edit" ? sel : null) : null;
    if (bad) return bad;
    if (isLive && live) {
      const editing = form === "edit" ? sel : null;
      const body = withOwner(values, live.team, editing);
      return (async () => {
        try {
          if (editing) {
            const row = await live.crm.update(ent.src, editing, body);
            live.put(ent.src, row);
            say(`${cap(ent.noun)} ${tag(row)} saved`);
          } else {
            const row = await live.crm.create(ent.src, { ...(ent.initial ?? {}), ...body });
            live.put(ent.src, row);
            setSelId(row.id);
            say(`${tag(row)} created`);
          }
          setForm(null);
          return null;
        } catch (e) {
          if (errStatus(e) === 409 || errStatus(e) === 412) void live.reload([ent.src]);
          return apiMsg(e);
        }
      })();
    }
    if (form === "edit" && sel) {
      setData((d) => audit({ ...d, [ent.src]: rows(d, ent.src).map((r) => (r.id === sel.id ? ({ ...r, ...values } as Row) : r)) }, ent.title, sel.id, "Edited", "Record changed"));
      say(`${cap(ent.noun)} ${sel.id} saved`);
    } else {
      const id = nextId(ent.prefix ?? "", list);
      const row: Row = { ...values, ...(ent.initial ?? {}), id, ...(ent.src === "leads" ? { created: off(0) } : {}) };
      setData((d) => audit({ ...d, [ent.src]: [...rows(d, ent.src), row] }, ent.title, id, "Created", `${id} created`));
      setSelId(id);
      say(`${id} created`);
    }
    setForm(null);
    return null;
  };
  const tryDelete = () => {
    if (!sel) return;
    const why = ent.canDelete ? ent.canDelete(sel, data) : null;
    if (why) return say(why, true);
    setConfirmDel(true);
  };
  const remove = () => {
    if (!sel) return;
    if (isLive && live) {
      const row = sel;
      setConfirmDel(false);
      void (async () => {
        try {
          await live.crm.remove(ent.src, row);
          live.drop(ent.src, row.id);
          setSelId(null);
          say(`${tag(row)} deleted`);
        } catch (e) { say(apiMsg(e), true); }
      })();
      return;
    }
    setData((d) => audit({ ...d, [ent.src]: rows(d, ent.src).filter((r) => r.id !== sel.id) }, ent.title, sel.id, "Deleted", `${sel.id} deleted`));
    say(`${sel.id} deleted`);
    setSelId(null);
    setConfirmDel(false);
  };
  const exportCsv = (only?: Row[]) => {
    const esc = (s: string) => `"${s.replace(/"/g, '""')}"`;
    const src = only ?? view;
    const lines = [ent.cols.map((c) => esc(c.label)).join(",")].concat(src.map((r) => ent.cols.map((c) => esc(show(c, val(c, r, data)))).join(",")));
    const url = URL.createObjectURL(new Blob([lines.join("\n")], { type: "text/csv" }));
    const a = document.createElement("a");
    a.href = url;
    a.download = `${ent.id.toLowerCase().replace(/\s+/g, "-")}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };
  const sortBy = (key: string) => { setPage(1); setSort((s) => (s && s.key === key ? (s.dir === 1 ? { key, dir: -1 } : null) : { key, dir: 1 })); };

  /* ----- many rows at once ----- */
  const toggleRow = (id: string, on: boolean) => setChecked((s) => { const n = new Set(s); if (on) n.add(id); else n.delete(id); return n; });
  const togglePage = (on: boolean) => setChecked((s) => { const n = new Set(s); slice.forEach((r) => (on ? n.add(r.id) : n.delete(r.id))); return n; });
  const bulkActs = acts
    .filter((a) => !a.prompt && !a.label.startsWith("Draft"))
    .map((a) => ({ a, rows: picked.filter((r) => a.show(r)) }))
    .filter((x) => x.rows.length > 0);
  const bulkRun = async (a: Act, targets: Row[]) => {
    if (bulkBusy) return;
    setBulkBusy(true);
    let ok = 0;
    let bad = 0;
    let first = "";
    for (const r of targets) {
      const m = await Promise.resolve(runAct(a, {}, r, true));
      if (m) { bad++; first = first || m; } else ok++;
    }
    setBulkBusy(false);
    if (bad) say(`${a.label}: ${ok} done, ${bad} failed. ${first}`, true);
    else { say(`${a.label}: ${ok} ${ok === 1 ? ent.noun : `${ent.noun}s`} updated`); setChecked(new Set()); }
  };
  const deletable = (r: Row): string | null => {
    if (ent.editable && !ent.editable.includes(String(r.status ?? r.stage))) return `${tag(r)} can't be changed in its current status.`;
    return ent.canDelete ? ent.canDelete(r, data) : null;
  };
  const bulkDelete = async () => {
    setBulkDel(false);
    const ok = picked.filter((r) => !deletable(r));
    const skipped = picked.length - ok.length;
    if (bulkBusy || ok.length === 0) return say(skipped ? "None of the selected records can be deleted." : "Nothing selected.", true);
    setBulkBusy(true);
    let done = 0;
    let bad = 0;
    let first = "";
    for (const r of ok) {
      try {
        if (isLive && live) { await live.crm.remove(ent.src, r); live.drop(ent.src, r.id); }
        else setData((d) => audit({ ...d, [ent.src]: rows(d, ent.src).filter((x) => x.id !== r.id) }, ent.title, r.id, "Deleted", `${r.id} deleted`));
        done++;
      } catch (e) { bad++; first = first || apiMsg(e); }
    }
    setBulkBusy(false);
    setChecked(new Set());
    if (selId && ok.some((r) => r.id === selId)) setSelId(null);
    say(`${done} deleted${skipped ? `, ${skipped} skipped` : ""}${bad ? `, ${bad} failed. ${first}` : ""}`, bad > 0 || done === 0);
  };

  /* ----- saved views (kept in this browser) ----- */
  const applyView = (name: string) => {
    setActiveView(name);
    const v = views.find((x) => x.name === name);
    setPage(1);
    setQ(v?.q ?? "");
    setFilterV(v?.filterV ?? "All");
    setSort(v?.sort ?? null);
    if (v && PAGE_SIZES.includes(v.pageSize)) setPageSize(v.pageSize);
  };
  const persistViews = (next: SavedView[]) => {
    setViews(next);
    try { localStorage.setItem(viewsKey, JSON.stringify(next)); } catch { say("This browser would not keep the view.", true); }
  };
  const saveView = () => {
    const name = (viewName ?? "").trim();
    if (!name) return;
    persistViews([...views.filter((v) => v.name !== name), { name, q, filterV, sort, pageSize }].sort((a, b) => a.name.localeCompare(b.name)));
    setActiveView(name);
    setViewName(null);
    say(`View "${name}" saved`);
  };
  const deleteView = () => {
    if (!activeView) return;
    persistViews(views.filter((v) => v.name !== activeView));
    setActiveView("");
    say(`View "${activeView}" deleted`);
  };

  const copyDraft = async () => {
    try {
      await navigator.clipboard.writeText(draft?.body ?? "");
      say("Draft copied");
    } catch {
      say("Copy isn't available in this browser", true);
    }
  };
  const logDraft = () => {
    if (!draft) return;
    const acc = rows(data, "accounts").find((a) => a.name === draft.account);
    setData((d) => audit({ ...d, activities: [...rows(d, "activities"), R(nextId("AT-", rows(d, "activities")), { type: "Email", subject: draft.title, account: acc?.id ?? "", date: off(0), owner: "You", status: "Completed" })] }, "Activity", "—", "Email logged", draft.title));
    say("Email logged as a completed activity");
    setDraft(null);
  };

  return (
    <>
      {tiles && <Tiles tiles={tiles} />}
      <div className={styles.crCard}>
        <div className={styles.crPane} role="toolbar" aria-label="Actions">
          {!ent.readOnly && (
            <>
              <Button size="small" className={styles.crPrimary} appearance="primary" icon={<Add20Regular />} onClick={() => setForm("new")}>New</Button>
              <Button size="small" appearance="subtle" icon={<Edit20Regular />} disabled={!canChange} onClick={() => setForm("edit")}>Edit</Button>
              <Button size="small" appearance="subtle" icon={<Delete20Regular />} disabled={!canChange} onClick={tryDelete}>Delete</Button>
            </>
          )}
          {!ent.readOnly && ent.acts.length > 0 && <span className={styles.crSep} />}
          {acts.map((a) => (
            <Button key={a.label} size="small" appearance="subtle" icon={a.icon as React.ReactElement} disabled={!sel || !a.show(sel)} onClick={() => { if (a.prompt) setPrompt(a); else void Promise.resolve(runAct(a, {})).then((m) => { if (m) say(m, true); }); }}>{a.label}</Button>
          ))}
          <span className={styles.crGrow} />
          <Button size="small" appearance="subtle" icon={<ArrowDownload20Regular />} onClick={() => exportCsv()}>Export</Button>
        </div>
        {picked.length > 0 && (
          <div className={styles.crBulk} role="region" aria-label="Actions for the selected records">
            <strong>{picked.length} selected</strong>
            {bulkActs.map(({ a, rows: t }) => (
              <Button key={a.label} size="small" appearance="subtle" disabled={bulkBusy} icon={a.icon as React.ReactElement} onClick={() => void bulkRun(a, t)}>{a.label} ({t.length})</Button>
            ))}
            {!ent.readOnly && <Button size="small" appearance="subtle" disabled={bulkBusy} icon={<Delete20Regular />} onClick={() => setBulkDel(true)}>Delete</Button>}
            <Button size="small" appearance="subtle" icon={<ArrowDownload20Regular />} onClick={() => exportCsv(picked)}>Export selected</Button>
            <span className={styles.crGrow} />
            <Button size="small" appearance="subtle" onClick={() => setChecked(new Set())}>Clear selection</Button>
          </div>
        )}
        <div className={styles.crFilters}>
          <SearchBox className={styles.crSearch} placeholder={`Search ${ent.title.toLowerCase()}`} value={q} onChange={(_, dd) => { setPage(1); setActiveView(""); setQ(dd.value); }} />
          {filterCol && (
            <Select size="small" className={styles.crSel} value={filterV} onChange={(_, dd) => { setPage(1); setActiveView(""); setFilterV(dd.value); }} aria-label="Filter">
              <option value="All">{ent.filterLabel ?? "All statuses"}</option>
              {choices.map((s) => <option key={s}>{s}</option>)}
            </Select>
          )}
          <Select size="small" className={styles.crSel} value={activeView} onChange={(_, dd) => applyView(dd.value)} aria-label="Saved views">
            <option value="">Views</option>
            {views.map((v) => <option key={v.name} value={v.name}>{v.name}</option>)}
          </Select>
          <Button size="small" appearance="subtle" icon={<Save16Regular />} onClick={() => setViewName(activeView)}>Save view</Button>
          {activeView && <Button size="small" appearance="subtle" icon={<Delete16Regular />} onClick={deleteView}>Delete view</Button>}
        </div>
        <div className={styles.crWork}>
          <div className={styles.crGridWrap}>
            <table className={styles.crGrid}>
              <thead>
                <tr>
                  <th className={styles.crChk}>
                    <Checkbox aria-label="Select all on this page" checked={onPage === 0 ? false : onPage === slice.length ? true : "mixed"} onChange={(_, d) => togglePage(d.checked === true)} />
                  </th>
                  {ent.cols.map((c) => (
                    <th key={c.key} className={c.right ? styles.crRight : undefined} aria-sort={sort?.key === c.key ? (sort.dir === 1 ? "ascending" : "descending") : "none"}>
                      <button type="button" className={styles.crTh} onClick={() => sortBy(c.key)}>
                        {c.label}
                        {sort?.key === c.key && (sort.dir === 1 ? <ChevronUp12Regular /> : <ChevronDown12Regular />)}
                      </button>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {slice.map((r) => (
                  <tr key={r.id} className={r.id === selId ? styles.crRowSel : undefined} aria-selected={r.id === selId} tabIndex={0} onClick={() => setSelId(r.id)} onKeyDown={(e) => e.target === e.currentTarget && (e.key === "Enter" || e.key === " ") && (e.preventDefault(), setSelId(r.id))}>
                    <td className={styles.crChk} onClick={(e) => e.stopPropagation()}>
                      <Checkbox aria-label={`Select ${tag(r)}`} checked={checked.has(r.id)} onChange={(_, d) => toggleRow(r.id, d.checked === true)} />
                    </td>
                    {ent.cols.map((c) => {
                      const v = val(c, r, data);
                      return (
                        <td key={c.key} className={`${c.right ? styles.crRight : ""} ${c.kind === "mono" ? styles.crMono : ""}`}>
                          {c.kind === "status" ? (v ? <Pill text={String(v)} /> : null) : c.kind === "bar" ? <UseBar pct={num(v)} /> : show(c, v)}
                        </td>
                      );
                    })}
                  </tr>
                ))}
                {view.length === 0 && <tr><td colSpan={ent.cols.length + 1} className={styles.crEmpty}>Nothing here yet.</td></tr>}
              </tbody>
            </table>
          </div>
          {sel && (
            <aside className={styles.crDetail} aria-label="Details">
              <div className={styles.crDetailHead}>
                <strong>{tag(sel)}</strong>
                <Button size="small" appearance="subtle" icon={<Dismiss20Regular />} aria-label="Close details" onClick={() => setSelId(null)} />
              </div>
              <dl className={styles.crDl}>
                {ent.cols.filter((c) => c.key !== "id").map((c) => {
                  const v = val(c, sel, data);
                  return (
                    <div key={c.key} className={styles.crDlRow}>
                      <dt>{c.label}</dt>
                      <dd>{c.kind === "status" ? (v ? <Pill text={String(v)} /> : "—") : c.kind === "bar" ? <UseBar pct={num(v)} /> : show(c, v)}</dd>
                    </div>
                  );
                })}
              </dl>
              {ent.detail && ent.detail(sel, data)}
              <RecordExtras key={sel.id} ent={ent} rec={sel} data={data} live={isLive} say={say} />
            </aside>
          )}
        </div>
        <div className={`${styles.crFoot} ${styles.crPager}`}>
          <span>{view.length ? `${from}–${to} of ${view.length}` : "0 records"}{view.length !== list.length ? ` (filtered from ${list.length})` : ""}</span>
          <span className={styles.crGrow} />
          <label className={styles.crPageSize}>Rows per page
            <Select size="small" value={String(pageSize)} onChange={(_, dd) => { setPage(1); setPageSize(Number(dd.value) || 25); }} aria-label="Rows per page">
              {PAGE_SIZES.map((n) => <option key={n} value={n}>{n}</option>)}
            </Select>
          </label>
          <Button size="small" appearance="subtle" icon={<ChevronLeft16Regular />} aria-label="Previous page" disabled={cur <= 1} onClick={() => setPage(cur - 1)} />
          <span>Page {cur} of {pages}</span>
          <Button size="small" appearance="subtle" icon={<ChevronRight16Regular />} aria-label="Next page" disabled={cur >= pages} onClick={() => setPage(cur + 1)} />
        </div>
      </div>

      {form && <RecordDialog key={`${form}${sel?.id ?? ""}`} title={form === "edit" ? `Edit ${ent.noun}` : `New ${ent.noun}`} submit={form === "edit" ? "Save" : "Create"} fields={formFields} data={data} initial={form === "edit" && sel ? (sel as Record<string, Val>) : {}} onClose={() => setForm(null)} onSave={save} />}
      {prompt && prompt.prompt && sel && (
        <RecordDialog key={prompt.label} title={prompt.prompt.title} submit={prompt.prompt.submit} fields={prompt.prompt.fields} data={data} initial={prompt.prompt.init(sel, data)} onClose={() => setPrompt(null)}
          onSave={(vals) => { const bad = runAct(prompt, vals); if (bad && typeof bad !== "string") return bad.then((m) => { if (!m) setPrompt(null); return m; }); if (!bad) setPrompt(null); return bad; }} />
      )}
      <Dialog open={!!draft} onOpenChange={(_, dd) => !dd.open && setDraft(null)}>
        <DialogSurface className={styles.crDialog}>
          <DialogBody>
            <DialogTitle action={<DialogTrigger action="close"><Button size="small" appearance="subtle" aria-label="Close" icon={<Dismiss24Regular />} /></DialogTrigger>}>
              <span className={styles.crAiHead}><Sparkle20Regular /> {draft?.title}</span>
            </DialogTitle>
            <DialogContent>
              <pre className={styles.crDraft}>{draft?.body}</pre>
              <p className={styles.crHint}>AI-written draft. Review it before sending.</p>
            </DialogContent>
            <DialogActions>
              <Button size="small" icon={<Copy20Regular />} onClick={copyDraft}>Copy</Button>
              <Button size="small" className={styles.crPrimary} appearance="primary" onClick={logDraft}>Log as sent email</Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>
      <Dialog open={confirmDel} onOpenChange={(_, dd) => !dd.open && setConfirmDel(false)}>
        <DialogSurface className={styles.crDialogSm}>
          <DialogBody>
            <DialogTitle>Delete {ent.noun}?</DialogTitle>
            <DialogContent>{sel ? `${tag(sel)} will be removed. This can't be undone.` : ""}</DialogContent>
            <DialogActions>
              <DialogTrigger action="close"><Button size="small">Cancel</Button></DialogTrigger>
              <Button size="small" className={styles.crDanger} appearance="primary" onClick={remove}>Delete</Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>
      <Dialog open={viewName !== null} onOpenChange={(_, dd) => !dd.open && setViewName(null)}>
        <DialogSurface className={styles.crDialogSm}>
          <DialogBody>
            <DialogTitle>Save this view</DialogTitle>
            <DialogContent>
              <Field label="View name" hint="Keeps the search, filter, sort and page size. Saved in this browser only.">
                <Input value={viewName ?? ""} onChange={(_, dd) => setViewName(dd.value)} onKeyDown={(e) => e.key === "Enter" && saveView()} maxLength={60} />
              </Field>
            </DialogContent>
            <DialogActions>
              <DialogTrigger action="close"><Button size="small">Cancel</Button></DialogTrigger>
              <Button size="small" className={styles.crPrimary} appearance="primary" disabled={!(viewName ?? "").trim()} onClick={saveView}>Save view</Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>
      <Dialog open={bulkDel} onOpenChange={(_, dd) => !dd.open && setBulkDel(false)}>
        <DialogSurface className={styles.crDialogSm}>
          <DialogBody>
            <DialogTitle>Delete {picked.length} {picked.length === 1 ? ent.noun : `${ent.noun}s`}?</DialogTitle>
            <DialogContent>
              {picked.filter((r) => !deletable(r)).length} will be removed. This can't be undone.
              {picked.filter((r) => deletable(r)).length > 0 && ` ${picked.filter((r) => deletable(r)).length} can't be deleted and will be skipped.`}
            </DialogContent>
            <DialogActions>
              <DialogTrigger action="close"><Button size="small">Cancel</Button></DialogTrigger>
              <Button size="small" className={styles.crDanger} appearance="primary" onClick={() => void bulkDelete()}>Delete</Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>
    </>
  );
};

/* ---------- pipeline board ---------- */
const BoardPage: React.FC<PageProps> = ({ data, setData, say, live }) => {
  const [dragId, setDragId] = useState<string | null>(null);
  const cols = ["Qualify", "Develop", "Propose", "Negotiate", "Won"];
  const move = (o: Row, to: string) => {
    if (o.stage === to) return;
    if (live) {
      void (async () => {
        try {
          const row = to === "Won" ? await live.crm.action("opportunities", o, "win") : await live.crm.action("opportunities", o, "stage", { stage: to });
          if (row.id) live.put("opportunities", row);
          else await live.reload(["opportunities"]);
          say(`${String(o.name)} moved to ${to}`);
        } catch (e) { say(apiMsg(e), true); void live.reload(["opportunities"]); }
      })();
      return;
    }
    setData((d) => audit({ ...d, opportunities: rows(d, "opportunities").map((x) => (x.id === o.id ? { ...x, stage: to } : x)) }, "Pipeline", o.id, `Moved to ${to}`, `${String(o.name)} moved from ${String(o.stage)} to ${to}`));
    say(`${String(o.name)} moved to ${to}`);
  };
  const opps = rows(data, "opportunities");
  return (
    <>
      <Tiles tiles={[
        { label: "Open pipeline", value: money(sum(opps.filter(isOpenOpp), (o) => num(o.amount))), note: `${opps.filter(isOpenOpp).length} deals` },
        { label: "Weighted", value: money(sum(opps.filter(isOpenOpp), (o) => (num(o.amount) * prob(o)) / 100)) },
        { label: "At risk", value: String(opps.filter((o) => health(o, data) === "At risk").length), note: "Needs a follow-up" },
      ]} />
      <div className={styles.crBoard}>
        {cols.map((c) => {
          const list = opps.filter((o) => o.stage === c);
          return (
            <section key={c} className={styles.crLane} aria-label={c} onDragOver={(e) => e.preventDefault()} onDrop={() => { const o = opps.find((x) => x.id === dragId); if (o) move(o, c); setDragId(null); }}>
              <header className={styles.crLaneHead}>
                <span>{c}</span>
                <span className={styles.crLaneSum}>{compact(sum(list, (o) => num(o.amount)))} · {list.length}</span>
              </header>
              <div className={styles.crLaneBody}>
                {list.map((o) => {
                  const i = cols.indexOf(c);
                  const h = health(o, data);
                  return (
                    <article key={o.id} className={styles.crDeal} draggable onDragStart={() => setDragId(o.id)}>
                      <strong>{String(o.name)}</strong>
                      <span className={styles.crHint}>{nameOf(data, "accounts", o.account)}</span>
                      <span className={styles.crDealRow}><b>{money(num(o.amount))}</b>{h ? <Pill text={h} /> : null}</span>
                      <span className={styles.crHint}>Close {fdate(String(o.close))} · {String(o.owner)}</span>
                      <span className={styles.crDealBtns}>
                        <Button size="small" appearance="subtle" icon={<ChevronLeft16Regular />} aria-label={`Move ${String(o.name)} back`} disabled={i === 0} onClick={() => move(o, cols[i - 1] ?? c)} />
                        <Button size="small" appearance="subtle" icon={<ChevronRight16Regular />} aria-label={`Move ${String(o.name)} forward`} disabled={i === cols.length - 1} onClick={() => move(o, cols[i + 1] ?? c)} />
                      </span>
                    </article>
                  );
                })}
                {list.length === 0 && <div className={styles.crEmpty}>No deals.</div>}
              </div>
            </section>
          );
        })}
      </div>
    </>
  );
};

/* ---------- forecast ---------- */
const Panel: React.FC<{ title: string; note?: string; children: React.ReactNode; wide?: boolean }> = ({ title, note, children, wide }) => (
  <section className={`${styles.crPanel} ${wide ? styles.crWide : ""}`}>
    <h3 className={styles.crPanelTitle}>{title}</h3>
    {note && <p className={styles.crPanelNote}>{note}</p>}
    {children}
  </section>
);
const ForecastPage: React.FC<{ data: Data }> = ({ data }) => {
  const opps = rows(data, "opportunities");
  const open = opps.filter(isOpenOpp);
  const wonYear = sum(opps.filter((o) => o.stage === "Won"), (o) => num(o.amount));
  const quota = sum(rows(data, "team"), (m) => num(m.quota));
  const months = Array.from({ length: 6 }, (_, i) => {
    const dt = new Date();
    const start = isoD(new Date(dt.getFullYear(), dt.getMonth() + i, 1));
    const end = isoD(new Date(dt.getFullYear(), dt.getMonth() + i + 1, 0));
    const inM = open.filter((o) => String(o.close) >= start && String(o.close) <= end);
    return { label: parseD(start).toLocaleDateString("en-GB", { month: "short" }), total: sum(inM, (o) => num(o.amount)), weighted: sum(inM, (o) => (num(o.amount) * prob(o)) / 100) };
  });
  const max = Math.max(...months.map((m) => m.total), 1);
  return (
    <>
      <Tiles tiles={[
        { label: "Open pipeline", value: money(sum(open, (o) => num(o.amount))), note: `${open.length} deals` },
        { label: "Weighted forecast", value: money(sum(open, (o) => (num(o.amount) * prob(o)) / 100)), note: "Amount × probability" },
        { label: "Won", value: money(wonYear), note: "Closed won" },
        { label: "Quota attainment", value: quota ? `${Math.round((wonYear / quota) * 100)}%` : "n/a", note: `Quota ${compact(quota)}` },
      ]} />
      <div className={styles.crGrid2}>
        <Panel title="Forecast by close month" note="Next six months">
          <div className={styles.crLegend}><span><i className={styles.crDotA} />Weighted</span><span><i className={styles.crDotB} />Unweighted</span></div>
          <div className={styles.crChart} role="img" aria-label={months.map((m) => `${m.label}: weighted ${money(m.weighted)}, total ${money(m.total)}`).join("; ")}>
            {months.map((m) => (
              <div key={m.label} className={styles.crChartCol}>
                <div className={styles.crChartBars}>
                  <span className={`${styles.crChartBar} ${styles.crBarA}`} style={{ height: m.weighted > 0 ? `${Math.max((m.weighted / max) * 100, 3)}%` : "0" }} title={`Weighted ${money(m.weighted)}`} />
                  <span className={`${styles.crChartBar} ${styles.crBarB}`} style={{ height: m.total > 0 ? `${Math.max((m.total / max) * 100, 3)}%` : "0" }} title={`Total ${money(m.total)}`} />
                </div>
                <span className={styles.crChartLabel}>{m.label}</span>
              </div>
            ))}
          </div>
        </Panel>
        <Panel title="AI insights" note="Generated from your live pipeline, leads, tasks and cases">
          <ul className={styles.crInsights}>{insights(data).map((s) => <li key={s}><Sparkle20Regular />{s}</li>)}</ul>
        </Panel>
        <Panel title="By stage" wide>
          <table className={styles.crGrid}>
            <thead><tr>
              <th><span className={styles.crTh}>Stage</span></th><th className={styles.crRight}><span className={styles.crTh}>Deals</span></th><th className={styles.crRight}><span className={styles.crTh}>Value</span></th>
              <th className={styles.crRight}><span className={styles.crTh}>Probability</span></th><th className={styles.crRight}><span className={styles.crTh}>Weighted</span></th>
            </tr></thead>
            <tbody>
              {["Qualify", "Develop", "Propose", "Negotiate"].map((s) => {
                const l = opps.filter((o) => o.stage === s);
                return <tr key={s}><td><Pill text={s} /></td><td className={styles.crRight}>{l.length}</td><td className={styles.crRight}>{money(sum(l, (o) => num(o.amount)))}</td><td className={styles.crRight}>{PROB[s]}%</td><td className={styles.crRight}>{money(sum(l, (o) => (num(o.amount) * prob(o)) / 100))}</td></tr>;
              })}
            </tbody>
          </table>
        </Panel>
      </div>
    </>
  );
};

/* ---------- AI assistant ---------- */
const SUGGESTED = ["What should I focus on today?", "Which deals are at risk?", "Who are my hottest leads?", "Show the forecast", "Summarise Zenith Bank", "Draft an email to Femi Balogun", "How are my support cases?", "Quota attainment"];
const AssistantPage: React.FC<PageProps> = ({ data, setData }) => {
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const endRef = useRef<HTMLDivElement>(null);
  const msgs = rows(data, "chat");
  useEffect(() => { endRef.current?.scrollIntoView?.({ block: "end" }); }, [msgs.length, busy]);

  const send = (q: string) => {
    const t = q.trim();
    if (!t || busy) return;
    setText("");
    setBusy(true);
    setData((d) => ({ ...d, chat: [...rows(d, "chat"), R(`M-${rows(d, "chat").length + 1}`, { role: "user", text: t })] }));
    window.setTimeout(() => {
      setData((d) => ({ ...d, chat: [...rows(d, "chat"), R(`M-${rows(d, "chat").length + 1}`, { role: "ai", text: askAssistant(t, d) })] }));
      setBusy(false);
    }, 500);
  };
  const tasks = rows(data, "activities").filter((a) => a.status === "Open" && String(a.date) <= off(0)).slice(0, 4);
  return (
    <div className={styles.crAiPage}>
      <div className={`${styles.crCard} ${styles.crChat}`}>
        <div className={styles.crMsgs} role="log" aria-live="polite" aria-label="Conversation">
          {msgs.map((m) => (
            <div key={m.id} className={`${styles.crMsg} ${m.role === "user" ? styles.crMsgUser : styles.crMsgAi}`}>
              {m.role === "ai" && <span className={styles.crAvatar}><Sparkle20Regular /></span>}
              <div className={styles.crBubble}>{String(m.text)}</div>
            </div>
          ))}
          {busy && <div className={`${styles.crMsg} ${styles.crMsgAi}`}><span className={styles.crAvatar}><Sparkle20Regular /></span><div className={styles.crBubble}>Thinking…</div></div>}
          <div ref={endRef} />
        </div>
        <form className={styles.crAsk} onSubmit={(e) => { e.preventDefault(); send(text); }}>
          <Input className={styles.crAskInput} placeholder="Ask about deals, leads, accounts or cases" value={text} onChange={(_, dd) => setText(dd.value)} aria-label="Ask the assistant" />
          <Button size="small" type="submit" className={styles.crPrimary} appearance="primary" icon={<Send20Regular />} disabled={!text.trim() || busy}>Send</Button>
        </form>
      </div>
      <div className={styles.crSide}>
        <Panel title="Try asking">
          <div className={styles.crChips}>{SUGGESTED.map((s) => <button key={s} type="button" className={styles.crChip} onClick={() => send(s)}>{s}</button>)}</div>
        </Panel>
        <Panel title="Today's priorities">
          <ul className={styles.crInsights}>
            {tasks.map((t) => <li key={t.id}><CalendarClock20Regular />{String(t.type)}: {String(t.subject)}</li>)}
            {insights(data).slice(0, 2).map((s) => <li key={s}><Sparkle20Regular />{s}</li>)}
          </ul>
        </Panel>
      </div>
    </div>
  );
};

/* ---------- navigation ---------- */
interface NavItem { id: string; title: string; icon: React.ReactElement }
const NAV: { group: string; items: NavItem[] }[] = [
  { group: "My Work", items: [
    { id: "Sales accelerator", title: "Sales accelerator", icon: <TopSpeedRegular /> },
    { id: "Activity", title: "Activity", icon: <NoteEditRegular /> },
    { id: "Dashboard", title: "Dashboard", icon: <AppTitleRegular /> },
    { id: "AI assistant", title: "AI assistant", icon: <SparkleRegular /> },
  ] },
  { group: "Customer", items: [
    { id: "Accounts", title: "Accounts", icon: <PersonAccountsRegular /> },
    { id: "Linked Contact", title: "Linked Contact", icon: <ContactCardLinkRegular /> },
  ] },
  { group: "Sales", items: [
    { id: "Leads", title: "Leads", icon: <SlideTextCallRegular /> },
    { id: "Opportunity", title: "Opportunity", icon: <NoteAddRegular /> },
    { id: "Pipeline", title: "Pipeline", icon: <DataFunnelRegular /> },
    { id: "Forecast", title: "Forecast", icon: <ChartMultipleRegular /> },
    { id: "Compatitors", title: "Compatitors", icon: <FolderPeopleRegular /> },
  ] },
  { group: "collateral", items: [
    { id: "Quotes", title: "Quotes", icon: <SlideTextCallRegular /> },
    { id: "Orders", title: "Orders", icon: <NoteAddRegular /> },
    { id: "Linked Invoices", title: "Linked Invoices", icon: <FolderPeopleRegular /> },
    { id: "Products", title: "Products", icon: <FolderPeopleRegular /> },
  ] },
  { group: "Marketing", items: [
    { id: "Campaigns", title: "Campaigns", icon: <MegaphoneRegular /> },
    { id: "Marketing lists", title: "Marketing lists", icon: <PeopleListRegular /> },
  ] },
  { group: "Service", items: [
    { id: "Cases", title: "Cases", icon: <HeadsetRegular /> },
    { id: "Knowledge base", title: "Knowledge base", icon: <BookRegular /> },
  ] },
  { group: "Admin", items: [
    { id: "Sales team", title: "Sales team", icon: <PeopleTeamRegular /> },
    { id: "Activity log", title: "Activity log", icon: <HistoryRegular /> },
    { id: "All records", title: "All records", icon: <PeopleListRegular /> },
  ] },
];

/* ---------- revenue, partner, service and data modules (one sidebar item per module; every screen of the module is a section) ----------
   k = the screen key in crmCatalog.ts (regenerate that file with tools/gen_catalog.py when endpoints change), l = the tab label. */
interface ModPage { id: string; title: string; subtitle: string; icon: React.ReactElement; sections: { k: string; l: string }[] }
const MOD_PAGES: ModPage[] = [
  { id: "Quote configurator", title: "CPQ", subtitle: "Configure, price and hand off complex quotes: models, rules, guided selling and negotiated prices.", icon: <NoteAddRegular />, sections: [{ k: "cpq.models", l: "Config models" }, { k: "cpq.configurations", l: "Configurations" }, { k: "cpq.sessions", l: "Guided sessions" }, { k: "cpq.negotiated-prices", l: "Negotiated prices" }, { k: "cpq.bom-items", l: "Bom items" }, { k: "cpq.design-links", l: "Design links" }, { k: "cpq.questions", l: "Guide questions" }, { k: "cpq.guide-rules", l: "Guide rules" }, { k: "cpq.handoffs", l: "Handoffs" }, { k: "cpq.features", l: "Model features" }, { k: "cpq.options", l: "Model options" }, { k: "cpq.rules", l: "Model rules" }] },
  { id: "Revenue", title: "Revenue", subtitle: "Usage metering, charges, statements, entitlements and revenue schedules for subscriptions.", icon: <DataFunnelRegular />, sections: [{ k: "revenue.meters", l: "Usage meters" }, { k: "revenue.charges", l: "Usage charges" }, { k: "revenue.usage", l: "Usage records" }, { k: "revenue.statements", l: "Usage statements" }, { k: "revenue.risk", l: "Churn assessments" }, { k: "revenue.entitlements", l: "Entitlements" }, { k: "revenue.entries", l: "Revenue entries" }, { k: "revenue.schedules", l: "Revenue schedules" }] },
  { id: "Incentives", title: "Incentives", subtitle: "Plans, quotas and credit rules, then the credits and statements they produce.", icon: <ChartMultipleRegular />, sections: [{ k: "incentives.plans", l: "Incentive plans" }, { k: "incentives.assignments", l: "Plan assignments" }, { k: "incentives.quotas", l: "Quotas" }, { k: "incentives.credit-rules", l: "Credit rules" }, { k: "incentives.credits", l: "Credit transactions" }, { k: "incentives.adjustments", l: "Incentive adjustments" }, { k: "incentives.statements", l: "Incentive statements" }] },
  { id: "Agreements", title: "Agreements", subtitle: "Templates, content blocks, signers and the obligations an agreement creates.", icon: <DocumentFlowchartRegular />, sections: [{ k: "agreements.agreements", l: "Agreements" }, { k: "agreements.templates", l: "Document templates" }, { k: "agreements.blocks", l: "Content blocks" }, { k: "agreements.signers", l: "Agreement signers" }, { k: "agreements.obligations", l: "Obligations" }] },
  { id: "Partners", title: "Partners", subtitle: "Partner tiers and certifications, deal registration, joint plans and market development funds.", icon: <FolderPeopleRegular />, sections: [{ k: "partners.partners", l: "Partners" }, { k: "partners.tiers", l: "Partner tiers" }, { k: "partners.registrations", l: "Deal registrations" }, { k: "partners.mdf-requests", l: "Mdf requests" }, { k: "partners.compliance", l: "Compliance items" }, { k: "partners.joint-plans", l: "Joint business plans" }, { k: "partners.mdf-claims", l: "Mdf claims" }, { k: "partners.funds", l: "Mdf funds" }, { k: "partners.opportunity-partners", l: "Opportunity partners" }, { k: "partners.assets", l: "Partner assets" }, { k: "partners.attributions", l: "Partner attributions" }, { k: "partners.certifications", l: "Partner certifications" }, { k: "partners.scorecards", l: "Partner scorecards" }] },
  { id: "Customer success", title: "Customer success", subtitle: "Health scores, success plans, playbooks, renewals, expansion and loyalty.", icon: <PersonAccountsRegular />, sections: [{ k: "success.health-scores", l: "Health scores" }, { k: "success.plans", l: "Success plans" }, { k: "success.playbooks", l: "Playbooks" }, { k: "success.renewals", l: "Renewal watches" }, { k: "success.expansions", l: "Expansion opportunities" }, { k: "success.signals", l: "Account signals" }, { k: "success.reviews", l: "Business reviews" }, { k: "success.members", l: "Loyalty members" }, { k: "success.programs", l: "Loyalty programs" }, { k: "success.loyalty-tiers", l: "Loyalty tiers" }, { k: "success.playbook-runs", l: "Playbook runs" }, { k: "success.redemptions", l: "Redemptions" }, { k: "success.rewards", l: "Rewards" }] },
  { id: "Field service", title: "Field service", subtitle: "Work orders, technicians, skills, parts and stock.", icon: <LocationRegular />, sections: [{ k: "fieldservice.work-orders", l: "Work orders" }, { k: "fieldservice.technicians", l: "Technicians" }, { k: "fieldservice.service-types", l: "Service types" }, { k: "fieldservice.skills", l: "Skills" }, { k: "fieldservice.parts", l: "Parts" }, { k: "fieldservice.stock", l: "Stock levels" }, { k: "fieldservice.stock-movements", l: "Stock movements" }, { k: "fieldservice.time-entries", l: "Time entries" }, { k: "fieldservice.time-off", l: "Time offs" }, { k: "fieldservice.warehouses", l: "Warehouses" }] },
  { id: "Data platform", title: "Data platform", subtitle: "Customer profiles, audiences, data sources and destinations, and merge suggestions.", icon: <DocumentDataLinkRegular />, sections: [{ k: "cdp.profiles", l: "Profiles" }, { k: "cdp.audiences", l: "Audiences" }, { k: "cdp.sources", l: "Data sources" }, { k: "cdp.destinations", l: "Destinations" }, { k: "cdp.activations", l: "Activation links" }, { k: "cdp.merge-suggestions", l: "Merge suggestions" }] },
  { id: "Conversation intelligence", title: "Conversation intelligence", subtitle: "Call analysis, objections, deal risk and coaching, built from the conversations you add.", icon: <SparkleRegular />, sections: [{ k: "intelligence.conversations", l: "Conversations" }, { k: "intelligence.rules", l: "Conversation rules" }, { k: "intelligence.objections", l: "Objection records" }, { k: "intelligence.deal-risks", l: "Deal risk assessments" }, { k: "intelligence.coaching", l: "Coaching insights" }, { k: "intelligence.captured-activities", l: "Captured activities" }, { k: "intelligence.field-updates", l: "Field updates" }, { k: "intelligence.competitors", l: "Competitors" }] },
];
const MOD_GROUPS: { group: string; ids: string[] }[] = [
  { group: "Revenue operations", ids: ["Quote configurator", "Revenue", "Incentives", "Agreements"] },
  { group: "Engagement", ids: ["Partners", "Customer success", "Field service", "Data platform", "Conversation intelligence"] },
];
const ModulePage: React.FC<{ m: ModPage }> = ({ m }) => {
  const [key, setKey] = useState<string>(m.sections[0]?.k ?? "");
  const cur = m.sections.find((x) => x.k === key) ?? m.sections[0];
  return (
    <section className={styles.crModWrap}>
      <nav className={styles.crModNav} aria-label={`${m.title} sections`}>
        <span className={styles.crModNavT}>{m.title}</span>
        {m.sections.map((x) => (
          <button key={x.k} type="button" aria-current={cur?.k === x.k ? "page" : undefined} className={`${styles.crModNavBtn} ${cur?.k === x.k ? styles.crModNavOn : ""}`} onClick={() => setKey(x.k)}>{x.l}</button>
        ))}
      </nav>
      <div className={styles.crModBody}>
        {cur ? <CrmEntityView key={cur.k} entityKey={cur.k} /> : <div className={styles.crHint}>This module has no screens yet.</div>}
      </div>
    </section>
  );
};

const CrmPage: React.FC<PageProps & { tab: string }> = ({ tab, ...p }) => {
  const ent = ENTS.find((e) => e.id === tab);
  if (!ent) {
    const m = MOD_PAGES.find((x) => x.id === tab);
    if (!m) return null;
    return (
      <div className={styles.crPg}>
        <header className={styles.crHead}>
          <h2 className={styles.crTitle}>{m.title}</h2>
          <p className={styles.crSub}>{m.subtitle}</p>
        </header>
        <ModulePage key={m.id} m={m} />
      </div>
    );
  }
  return (
    <div className={styles.crPg}>
      <header className={styles.crHead}>
        <h2 className={styles.crTitle}>{ent.title}</h2>
        <p className={styles.crSub}>{ent.subtitle}</p>
      </header>
      {ent.page === "grid" && p.live && SERVER_ONLY[ent.src] && <CrmEntityView entityKey={SERVER_ONLY[ent.src]} />}
      {ent.page === "grid" && !(p.live && SERVER_ONLY[ent.src]) && <EntityPage key={ent.id} ent={ent} {...p} />}
      {ent.page === "board" && <BoardPage {...p} />}
      {ent.page === "forecast" && <ForecastPage data={p.data} />}
      {ent.page === "ai" && <AssistantPage {...p} />}
    </div>
  );
};

/* =====================================================================
   Empty data set: every list starts empty and is filled from the CRM service
   ===================================================================== */
const DATA_KEYS = ["team", "accounts", "contacts", "products", "opportunities", "leads", "activities", "competitors", "quotes", "orders", "invoices",
  "campaigns", "lists", "cases", "articles", "notes", "audit"];
const emptyData = (): Data => ({ ...Object.fromEntries(DATA_KEYS.map((k) => [k, [] as Row[]])), chat: [R("M-1", { role: "ai", text: "Hi, I'm your CRM assistant. Ask me what to focus on today, which deals are at risk, who your hottest leads are, or to summarise an account." })] });

/* =====================================================================
   Contact pane (Sales accelerator, right side): toolbar, tabs and pages
   ===================================================================== */
const SUBTABS = ["Summary", "Analytics", "Details", "Related"] as const;
type SubTab = (typeof SUBTABS)[number];
const EXTRA: Record<string, Record<string, string>> = {
  "1": { email: "skelvin@uba.com", phone: "+234 8180 897 8765", title: "General Manager/CEO", mobile: "+234 8180 897 8765" },
};
const accountFor = (tab: WorkflowTab, d: Data): Row | undefined => {
  const byName = rows(d, "accounts").find((a) => String(a.name).toLowerCase() === tab.company.toLowerCase());
  if (byName) return byName;
  const c = rows(d, "contacts").find((x) => x.name === tab.name);
  return c ? rows(d, "accounts").find((a) => a.id === c.account) : undefined;
};
const profileOf = (tab: WorkflowTab, d: Data): Row => {
  const saved = rows(d, "profiles").find((p) => p.id === tab.id);
  const c = rows(d, "contacts").find((x) => x.name === tab.name);
  const acct = accountFor(tab, d);
  const parts = tab.name.split(" ");
  const ex = EXTRA[tab.id] ?? {};
  const base = R(tab.id, {
    first: parts[0] ?? "", middle: "", last: parts.slice(1).join(" "), title: ex.title ?? String(c?.title ?? tab.role), dept: "",
    email: ex.email ?? String(c?.email ?? ""), phone: ex.phone ?? String(c?.phone ?? ""), mobile: ex.mobile ?? "", fax: "", address: "",
    city: String(acct?.city ?? ""), owner: String(acct?.owner ?? ""), source: "Referral", channel: "Email", status: String(c?.status ?? "Active"),
    dnEmail: "No", dnPhone: "No", desc: "", topic: "", website: "", state: "", country: "Nigeria", zip: "", budgetStatus: "May buy", need: "Should have", timeframe: "This quarter", process: "Committee", decider: "No", estRevenue: 0, stage: "Qualify", stageSince: off(-2), qualified: "No",
  });
  return saved ? ({ ...base, ...saved } as Row) : base;
};
const ACT_ICON: Record<string, React.ReactNode> = { Call: <Phone20Regular />, Email: <Send20Regular />, Meeting: <CalendarClock20Regular />, Task: <Checkmark20Regular />, SMS: <Send20Regular />, Chat: <Send20Regular /> };

const DCard: React.FC<{ title: string; note?: string; action?: React.ReactNode; wide?: boolean; children: React.ReactNode }> = ({ title, note, action, wide, children }) => (
  <section className={`${styles.crDtCard} ${wide ? styles.crWide : ""}`}>
    <header className={styles.crDtCardHead}><h3>{title}</h3>{action}</header>
    {note && <p className={styles.crPanelNote}>{note}</p>}
    {children}
  </section>
);
const contactScore = (p: Row, touches: number, idle: number) => {
  const pos: string[] = [];
  const neg: string[] = [];
  let s = 20;
  const src = SOURCE_PTS[String(p.source)] ?? 5;
  s += src;
  (src >= 15 ? pos : neg).push(`${String(p.source)} source`);
  const bud = { "Will buy": 25, "Can buy": 18, "May buy": 8 }[String(p.budgetStatus)] ?? 0;
  s += bud;
  (bud >= 18 ? pos : neg).push(bud ? `Budget: ${String(p.budgetStatus)}` : "No committed budget");
  const tf = { Immediate: 15, "This quarter": 10, "Next quarter": 5 }[String(p.timeframe)] ?? 0;
  s += tf;
  (tf >= 10 ? pos : neg).push(tf ? `Buying ${String(p.timeframe).toLowerCase()}` : "No purchase timeframe");
  if (p.decider === "Yes") { s += 10; pos.push("Decision maker"); }
  if (touches) { s += Math.min(3, touches) * 5; pos.push(`${touches} completed touches`); } else neg.push("No completed touches");
  if (idle > 14 && idle < 99) { s -= 10; neg.push(`No contact for ${idle} days`); }
  const score = Math.max(0, Math.min(100, s));
  return { score, grade: score >= 75 ? "A" : score >= 55 ? "B" : score >= 35 ? "C" : "D", trend: idle <= 7 ? "Improving" : idle > 14 ? "Declining" : "Steady", pos: pos.slice(0, 4), neg: neg.slice(0, 3) };
};

const CH_COLOR: Record<string, string> = { Call: "#0f6cbd", Email: "#0e8a82", Meeting: "#c98a00", Task: "#5b6b8c", SMS: "#107c10", Chat: "#d83b01" };
const MiniTable: React.FC<{ head: string[]; body: (string | React.ReactNode)[][]; empty: string }> = ({ head, body, empty }) =>
  body.length === 0 ? (
    <p className={styles.crHint}>{empty}</p>
  ) : (
    <table className={styles.crGrid}>
      <thead><tr>{head.map((h) => <th key={h}><span className={styles.crTh}>{h}</span></th>)}</tr></thead>
      <tbody>{body.map((r, i) => <tr key={i}>{r.map((c, j) => <td key={j}>{c}</td>)}</tr>)}</tbody>
    </table>
  );
const Kv: React.FC<{ k: string; children: React.ReactNode }> = ({ k, children }) => (
  <div className={styles.crKv}><span>{k}</span><b>{children}</b></div>
);

interface PaneProps {
  tab: WorkflowTab;
  data: Data;
  setData: React.Dispatch<React.SetStateAction<Data>>;
  say: (msg: string, err?: boolean) => void;
  persona: React.ReactNode;
  bar: { trackRef: React.RefObject<HTMLDivElement>; canLeft: boolean; canRight: boolean; onLeft: () => void; onRight: () => void };
  onRemove: (id: string) => void;
  goTo: (tab: string) => void;
}
type Dlg = { kind: "add" | "connect" | "list" | "assign" | "sms" | "chat" | "resched" | "stake" | "comp" | "prod"; ref?: Row } | null;

const ContactPane: React.FC<PaneProps> = ({ tab, data, setData, say, persona, bar, onRemove, goTo }) => {
  const [sub, setSub] = useState<SubTab>("Summary");
  const [dlg, setDlg] = useState<Dlg>(null);
  const [draftMail, setDraftMail] = useState<Draft | null>(null);
  const [confirmDel, setConfirmDel] = useState(false);
  const [note, setNote] = useState("");
  const saved = profileOf(tab, data);
  const [edit, setEdit] = useState<Record<string, string>>({});
  useEffect(() => { setEdit({}); }, [tab.id]);
  const profile: Row = { ...saved, ...edit } as Row;
  const dirty = Object.keys(edit).some((k) => String(edit[k]) !== String(saved[k] ?? ""));
  const acct = accountFor(tab, data);
  const set = (k: string, v: string) => setEdit((e) => ({ ...e, [k]: v }));
  const active = profile.status === "Active";
  const name = `${String(profile.first)} ${String(profile.last)}`.trim() || tab.name;

  const upsert = (patch: Record<string, Val>, action: string, msg: string) => {
    setData((d) => {
      const cur = profileOf(tab, d);
      const row = { ...cur, ...patch, id: tab.id } as Row;
      const list = rows(d, "profiles").filter((p) => p.id !== tab.id);
      return audit({ ...d, profiles: [...list, row] }, "Contacts", tab.id, action, msg);
    });
    say(msg);
  };
  const saveEdits = () => {
    if (!dirty) return say("Nothing to save");
    if (!String(profile.first).trim() || !String(profile.last).trim()) return say("First and last name are required.", true);
    upsert(edit, "Saved", `${name} saved`);
    setEdit({});
  };
  const addActivity = (v: Record<string, Val>, label: string, status: string, msg: string) => {
    setData((d) => audit({ ...d, activities: [...rows(d, "activities"), R(nextId("AT-", rows(d, "activities")), { type: String(v.type), subject: String(v.subject), account: acct?.id ?? "", date: String(v.date ?? off(0)), owner: String(profile.owner || "You"), status, lead: "", contact: tab.id })] }, "Activity", tab.id, label, msg));
    say(msg);
  };
  const patchActivity = (id: string, patch: Record<string, Val>, label: string, msg: string) => {
    setData((d) => audit({ ...d, activities: rows(d, "activities").map((a) => (a.id === id ? ({ ...a, ...patch } as Row) : a)) }, "Activity", id, label, msg));
    say(msg);
  };
  const addNote = () => {
    const t = note.trim();
    if (!t) return;
    setData((d) => audit({ ...d, notes: [...rows(d, "notes"), R(nextId("NT-", rows(d, "notes")), { contact: tab.id, text: t, date: off(0) })] }, "Contacts", tab.id, "Note added", t.slice(0, 60)));
    setNote("");
    say("Note added");
  };
  const vcard = () => `BEGIN:VCARD\nVERSION:3.0\nFN:${name}\nORG:${tab.company}\nTITLE:${String(profile.title)}\nEMAIL:${String(profile.email)}\nTEL:${String(profile.phone)}\nEND:VCARD`;
  const copyDetails = async () => {
    try { await navigator.clipboard.writeText(`${name}\n${String(profile.title)}, ${tab.company}\n${String(profile.email)}\n${String(profile.phone)}`); say("Contact details copied"); } catch { say("Copy isn't available in this browser", true); }
  };
  const downloadVcard = () => {
    const url = URL.createObjectURL(new Blob([vcard()], { type: "text/vcard" }));
    const a = document.createElement("a");
    a.href = url; a.download = `${name.replace(/\s+/g, "-").toLowerCase()}.vcf`; a.click(); URL.revokeObjectURL(url);
  };
  const mail = () => setDraftMail(draftEmail(name, tab.company, "our recent conversation", "Your sales team"));

  /* derived */
  const accId = acct?.id ?? "__none";
  const acts = rows(data, "activities").filter((a) => a.account === accId || a.contact === tab.id);
  const upcoming = acts.filter((a) => a.status === "Open").sort((a, b) => String(a.date).localeCompare(String(b.date)));
  const done = acts.filter((a) => a.status === "Completed");
  const opps = rows(data, "opportunities").filter((o) => o.account === accId);
  const openOpps = opps.filter(isOpenOpp);
  const idle = acct ? lastTouch(data, acct.id) : 99;
  const relHealth = idle > 14 ? "At risk" : idle > 7 ? "Needs attention" : "Healthy";
  const relScore = Math.max(5, Math.min(100, 45 + Math.min(done.length, 6) * 8 - (idle >= 99 ? 30 : Math.max(0, idle - 7) * 2) + (openOpps.length ? 10 : 0)));
  const notes = rows(data, "notes").filter((n) => n.contact === tab.id);
  const timeline = [
    ...done.map((a) => ({ k: a.id, date: String(a.date), icon: ACT_ICON[String(a.type)] ?? <Checkmark20Regular />, title: String(a.subject), sub: `${String(a.type)} · ${String(a.owner)}` })),
    ...notes.map((n) => ({ k: n.id, date: String(n.date), icon: <Edit20Regular />, title: String(n.text), sub: "Note" })),
  ].sort((a, b) => b.date.localeCompare(a.date)).slice(0, 12);
  const lists = rows(data, "memberships").filter((m) => m.contact === tab.id).map((m) => rows(data, "lists").find((l) => l.id === m.list)).filter((l): l is Row => !!l);

  /* ---------- pages ---------- */
  /* ---- D365-style lead summary ---- */
  const BPF = ["Qualify", "Develop", "Propose", "Close"];
  const stageIdx = Math.max(0, BPF.indexOf(String(profile.stage)));
  const qualified = profile.qualified === "Yes";
  const daysInStage = Math.max(0, daysBetween(String(profile.stageSince || off(0)), off(0)));
  const setStage = (i: number) => upsert({ stage: BPF[i] ?? "Qualify", stageSince: off(0) }, "Stage changed", `${name} moved to ${BPF[i] ?? "Qualify"}`);
  const qualifyLead = () => {
    const accIdNew = acct?.id ?? nextId("AC-", rows(data, "accounts"));
    setData((d) => {
      let next: Data = d;
      if (!acct) next = { ...next, accounts: [...rows(next, "accounts"), R(accIdNew, { name: tab.company, industry: "", city: String(profile.city), employees: 0, revenue: 0, owner: String(profile.owner), status: "Prospect" })] };
      next = { ...next, opportunities: [...rows(next, "opportunities"), R(nextId("OP-", rows(next, "opportunities")), { name: `${tab.company} opportunity`, account: accIdNew, amount: num(profile.estRevenue), close: off(45), stage: "Qualify", owner: String(profile.owner || "You") })] };
      const cur = profileOf(tab, next);
      next = { ...next, profiles: [...rows(next, "profiles").filter((p) => p.id !== tab.id), { ...cur, qualified: "Yes", id: tab.id } as Row] };
      return audit(next, "Contacts", tab.id, "Qualified", `${name} qualified, opportunity created`);
    });
    say(`${name} qualified. Opportunity created`);
  };
  const bpf = (
    <section className={styles.crDtCard} aria-label="Business process flow">
      <ol className={styles.crBpf}>
        {BPF.map((s, i) => (
          <li key={s} className={`${styles.crBpfStep} ${i < stageIdx || qualified ? styles.crBpfDone : ""} ${i === stageIdx && !qualified ? styles.crBpfOn : ""}`}>
            <button type="button" className={styles.crBpfBtn} onClick={() => setStage(i)} aria-current={i === stageIdx ? "step" : undefined} disabled={qualified}>
              <span className={styles.crBpfDot}>{i < stageIdx || qualified ? <Checkmark20Regular /> : i + 1}</span>
              <span>{s}</span>
              {i === stageIdx && !qualified && <small>{daysInStage} {daysInStage === 1 ? "day" : "days"}</small>}
            </button>
          </li>
        ))}
        <li className={styles.crBpfEnd}>
          {qualified ? <Pill text="Qualified" /> : stageIdx < BPF.length - 1 ? (
            <Button size="small" className={styles.crPrimary} appearance="primary" icon={<ArrowRight20Regular />} onClick={() => setStage(stageIdx + 1)}>Next stage</Button>
          ) : (
            <Button size="small" className={styles.crPrimary} appearance="primary" icon={<Checkmark20Regular />} onClick={qualifyLead}>Qualify</Button>
          )}
          {stageIdx > 0 && !qualified && <Button size="small" appearance="subtle" onClick={() => setStage(stageIdx - 1)}>Back</Button>}
        </li>
      </ol>
    </section>
  );

  const sc = contactScore(profile, done.length, idle);
  const rating = sc.score >= 70 ? "Hot" : sc.score >= 45 ? "Warm" : "Cold";
  const stake = rows(data, "contacts").filter((c) => acct && c.account === acct.id && c.name !== tab.name);
  const compLinks = rows(data, "competitorLinks").filter((c) => c.contact === tab.id);
  const interests = rows(data, "interests").filter((c) => c.contact === tab.id);
  const similar = tabsOf(data).filter((w) => w.id !== tab.id && w.company === tab.company);
  const team = Array.from(new Set([String(profile.owner), String(acct?.owner ?? "")].filter(Boolean))).map((n) => rows(data, "team").find((m) => m.name === n)).filter((m): m is Row => !!m);
  const filters = ["All", "Notes", "Call", "Email", "Meeting", "Task"];
  const [tlFilter, setTlFilter] = useState("All");
  const tlShown = timeline.filter((t) => tlFilter === "All" || (tlFilter === "Notes" ? t.sub === "Note" : t.sub.startsWith(tlFilter)));
  const addAct = (label: string, onClick: () => void) => <Button size="small" appearance="subtle" icon={<Add20Regular />} onClick={onClick}>{label}</Button>;
  const removeRow = (ent: string, id: string, msg: string) => { setData((d) => audit({ ...d, [ent]: rows(d, ent).filter((r) => r.id !== id) }, "Contacts", tab.id, "Removed", msg)); say(msg); };

  const summary = (
    <>
      {bpf}
      <div className={styles.crCols}>
        <div className={styles.crCol}>
          <DCard title="Summary" action={<Button size="small" appearance="subtle" icon={<Edit20Regular />} onClick={() => setSub("Details")}>Edit</Button>}>
            <div className={styles.crKvList}>
              <Kv k="Topic">{String(profile.topic) || `${tab.company} enquiry`}</Kv>
              <Kv k="Name">{name}</Kv>
              <Kv k="Job title">{String(profile.title) || "—"}</Kv>
              <Kv k="Company">{tab.company}</Kv>
              <Kv k="Email">{profile.email ? <a href={`mailto:${String(profile.email)}`}>{String(profile.email)}</a> : "—"}</Kv>
              <Kv k="Phone">{String(profile.phone) || "—"}</Kv>
              <Kv k="Mobile">{String(profile.mobile) || "—"}</Kv>
              <Kv k="Website">{profile.website ? <a href={`https://${String(profile.website).replace(/^https?:\/\//, "")}`} target="_blank" rel="noreferrer">{String(profile.website)}</a> : "—"}</Kv>
              <Kv k="Lead source">{String(profile.source)}</Kv>
              <Kv k="Rating"><Pill text={rating} /></Kv>
              <Kv k="Status"><Pill text={qualified ? "Qualified" : active ? "Active" : "Inactive"} /></Kv>
              <Kv k="Owner">{String(profile.owner) || "—"}</Kv>
            </div>
          </DCard>
          <DCard title="Address">
            <div className={styles.crKvList}>
              <Kv k="Street">{String(profile.address) || "—"}</Kv>
              <Kv k="City">{String(profile.city) || "—"}</Kv>
              <Kv k="State">{String(profile.state) || "—"}</Kv>
              <Kv k="Country">{String(profile.country) || "—"}</Kv>
              <Kv k="Postal code">{String(profile.zip) || "—"}</Kv>
            </div>
          </DCard>
          <DCard title="Marketing">
            <div className={styles.crKvList}>
              <Kv k="Preferred channel">{String(profile.channel)}</Kv>
              <Kv k="Do not email">{String(profile.dnEmail)}</Kv>
              <Kv k="Do not call">{String(profile.dnPhone)}</Kv>
              <Kv k="Marketing lists">{lists.length ? lists.map((l) => String(l.name)).join(", ") : "None"}</Kv>
            </div>
          </DCard>
          <DCard title="Sales team">
            {team.length === 0 && <p className={styles.crHint}>No owner assigned.</p>}
            <div className={styles.crStack}>{team.map((m) => <div key={m.id} className={styles.crOppRow}><div><strong>{String(m.name)}</strong><div className={styles.crHint}>{String(m.role)} · {String(m.region)}</div></div></div>)}</div>
          </DCard>
        </div>

        <div className={styles.crCol}>
          <DCard title="Coming up" note={`${upcoming.length} open ${upcoming.length === 1 ? "activity" : "activities"}`}>
            {upcoming.length === 0 && <p className={styles.crHint}>Nothing scheduled. Use Add New to plan a task.</p>}
            <div className={styles.crStack}>
              {upcoming.slice(0, 4).map((a) => (
                <article key={a.id} className={styles.crTask}>
                  <div className={styles.crTaskHead}>{ACT_ICON[String(a.type)]}<strong>{String(a.subject)}</strong></div>
                  <div className={styles.crHint}>{String(a.type)} · due {fdate(String(a.date))} {String(a.date) < off(0) ? <Pill text="Overdue" /> : null}</div>
                  <div className={styles.crTaskBtns}>
                    <Button size="small" appearance="subtle" icon={<Checkmark20Regular />} onClick={() => patchActivity(a.id, { status: "Completed" }, "Complete", `${String(a.subject)} completed`)}>Complete</Button>
                    <Button size="small" appearance="subtle" icon={<CalendarClock20Regular />} onClick={() => setDlg({ kind: "resched", ref: a })}>Reschedule</Button>
                    <Button size="small" appearance="subtle" icon={<Dismiss20Regular />} onClick={() => patchActivity(a.id, { status: "Cancelled" }, "Dismiss", `${String(a.subject)} dismissed`)}>Dismiss</Button>
                  </div>
                </article>
              ))}
            </div>
            <div className={styles.crQuick}>
              <Button size="small" icon={<Send20Regular />} onClick={mail}>Send mail (AI draft)</Button>
              <Button size="small" icon={<Phone20Regular />} onClick={() => setDlg({ kind: "sms" })}>Send SMS</Button>
              <Button size="small" icon={<Add20Regular />} onClick={() => setDlg({ kind: "chat" })}>Log chat</Button>
            </div>
          </DCard>
          <DCard title="Qualification" action={<Button size="small" appearance="subtle" icon={<Edit20Regular />} onClick={() => setSub("Details")}>Edit</Button>}>
            <div className={styles.crKvList}>
              <Kv k="Budget">{String(profile.budgetStatus)}</Kv>
              <Kv k="Need">{String(profile.need)}</Kv>
              <Kv k="Purchase timeframe">{String(profile.timeframe)}</Kv>
              <Kv k="Purchase process">{String(profile.process)}</Kv>
              <Kv k="Decision maker">{String(profile.decider)}</Kv>
              <Kv k="Est. revenue">{num(profile.estRevenue) ? money(num(profile.estRevenue)) : "—"}</Kv>
            </div>
          </DCard>
          <DCard title="Timeline">
            <div className={styles.crNoteRow}>
              <Input className={styles.crAskInput} contentBefore={<Edit20Regular />} placeholder="Enter a note.." value={note} onChange={(_, dd) => setNote(dd.value)} onKeyDown={(e) => e.key === "Enter" && addNote()} aria-label="Note" />
              <Button size="small" className={styles.crPrimary} appearance="primary" disabled={!note.trim()} onClick={addNote}>Add</Button>
            </div>
            <div className={styles.crChips}>
              {filters.map((f) => <button key={f} type="button" className={`${styles.crChip} ${tlFilter === f ? styles.crChipOn : ""}`} onClick={() => setTlFilter(f)}>{f}</button>)}
            </div>
            {tlShown.length === 0 && <p className={styles.crHint}>No history yet.</p>}
            <ol className={styles.crTl}>
              {tlShown.map((t) => (
                <li key={t.k}><span className={styles.crTlIcon}>{t.icon}</span><div><strong>{t.title}</strong><div className={styles.crHint}>{t.sub} · {fdate(t.date)}</div></div></li>
              ))}
            </ol>
          </DCard>
          <DCard title="Similar leads">
            {similar.length === 0 && <p className={styles.crHint}>No other people from {tab.company} in your work flow.</p>}
            <div className={styles.crStack}>{similar.map((w) => <div key={w.id} className={styles.crOppRow}><div><strong>{w.name}</strong><div className={styles.crHint}>{w.role}</div></div></div>)}</div>
          </DCard>
        </div>

        <div className={`${styles.crCol} ${styles.crCol3}`}>
          <DCard title="Lead score">
            <div className={styles.crScore}>
              <div className={styles.crRing} style={{ background: `conic-gradient(#0f6cbd ${sc.score * 3.6}deg, #e3e6ee 0)` }}><span>{sc.score}</span></div>
              <div>
                <div className={styles.crGrade}>Grade {sc.grade}</div>
                <div className={styles.crHint}>{sc.trend}</div>
                <Pill text={rating} />
              </div>
            </div>
            <h4 className={styles.crH4}>Top factors</h4>
            <ul className={styles.crFactors}>
              {sc.pos.map((x) => <li key={x} className={styles.crPos}>+ {x}</li>)}
              {sc.neg.map((x) => <li key={x} className={styles.crNeg}>− {x}</li>)}
            </ul>
          </DCard>
          <DCard title="Relationship status">
            <div className={styles.crRel}><Pill text={relHealth} /><UseBar pct={relScore} /></div>
            <p className={styles.crHint}>{idle >= 99 ? "No closed activities for this record yet." : `Last contact ${idle} ${idle === 1 ? "day" : "days"} ago.`}</p>
            <div className={styles.crAi}>
              <div className={styles.crAiHead}><Sparkle20Regular /> AI summary</div>
              <p>{acct ? accountSummary(acct, data) : `${tab.company} isn't in your accounts yet. Qualify this lead to create it.`}</p>
            </div>
            {acct && (
              <div className={styles.crCo}>
                <strong><BuildingTownhouseRegular /> {String(acct.name)}</strong>
                <span className={styles.crHint}>{String(acct.industry) || "—"} · {String(acct.city) || "—"} · {num(acct.employees).toLocaleString("en-NG")} employees</span>
              </div>
            )}
            <h4 className={styles.crH4}>Recent opportunities from this company</h4>
            {opps.length === 0 && <p className={styles.crHint}>No opportunities yet.</p>}
            <div className={styles.crStack}>
              {opps.slice(0, 3).map((o) => (
                <div key={o.id} className={styles.crOppRow}>
                  <div><strong>{String(o.name)}</strong><div className={styles.crHint}>{money(num(o.amount))} · close {fdate(String(o.close))}</div></div>
                  <Pill text={String(o.stage)} />
                </div>
              ))}
            </div>
            {opps.length > 0 && <Button size="small" appearance="subtle" onClick={() => goTo("Opportunity")}>Open opportunities</Button>}
          </DCard>
          <DCard title="Stakeholders" action={addAct("Add", () => setDlg({ kind: "stake" }))}>
            {stake.length === 0 && <p className={styles.crHint}>No other contacts at this company.</p>}
            <div className={styles.crStack}>{stake.map((c) => <div key={c.id} className={styles.crOppRow}><div><strong>{String(c.name)}</strong><div className={styles.crHint}>{String(c.title) || "—"}</div></div></div>)}</div>
          </DCard>
          <DCard title="Competitors" action={addAct("Add", () => setDlg({ kind: "comp" }))}>
            {compLinks.length === 0 && <p className={styles.crHint}>No competitors recorded.</p>}
            <div className={styles.crStack}>
              {compLinks.map((l) => {
                const c = rows(data, "competitors").find((x) => x.id === l.competitor);
                return <div key={l.id} className={styles.crOppRow}><div><strong>{String(c?.name ?? l.competitor)}</strong><div className={styles.crHint}>{String(c?.strengths ?? "")}</div></div><span className={styles.crRowEnd}>{c ? <Pill text={String(c.threat)} /> : null}<Button size="small" appearance="subtle" icon={<Dismiss20Regular />} aria-label="Remove competitor" onClick={() => removeRow("competitorLinks", l.id, "Competitor removed")} /></span></div>;
              })}
            </div>
          </DCard>
          <DCard title="Products of interest" action={addAct("Add", () => setDlg({ kind: "prod" }))}>
            {interests.length === 0 && <p className={styles.crHint}>No products added.</p>}
            <div className={styles.crStack}>
              {interests.map((l) => {
                const p = rows(data, "products").find((x) => x.id === l.product);
                return <div key={l.id} className={styles.crOppRow}><div><strong>{String(p?.name ?? l.product)}</strong><div className={styles.crHint}>{p ? money(num(p.price)) : ""}</div></div><Button size="small" appearance="subtle" icon={<Dismiss20Regular />} aria-label="Remove product" onClick={() => removeRow("interests", l.id, "Product removed")} /></div>;
              })}
            </div>
          </DCard>
        </div>
      </div>
    </>
  );

  const months = Array.from({ length: 6 }, (_, i) => {
    const dt = new Date(); dt.setMonth(dt.getMonth() - (5 - i), 1);
    const key = isoD(dt).slice(0, 7);
    return { label: dt.toLocaleDateString("en-GB", { month: "short" }), n: done.filter((a) => String(a.date).startsWith(key)).length };
  });
  const mx = Math.max(...months.map((m) => m.n), 1);
  const byType = ["Call", "Email", "Meeting", "Task", "SMS", "Chat"].map((t) => { const l = done.filter((a) => a.type === t); return { t, n: l.length, share: Math.round((l.length / Math.max(done.length, 1)) * 100), last: l.map((a) => String(a.date)).sort().reverse()[0] ?? "" }; });
  const analytics = (
    <>
      <Tiles tiles={[
        { label: "Engagement score", value: `${relScore}/100`, note: relHealth },
        { label: "Touches, all time", value: String(done.length), note: `${upcoming.length} planned` },
        { label: "Open deal value", value: money(sum(openOpps, (o) => num(o.amount))), note: `${openOpps.length} deals` },
        { label: "Won", value: money(sum(opps.filter((o) => o.stage === "Won"), (o) => num(o.amount))) },
      ]} />
      <div className={styles.crDtGrid}>
        <DCard title="Touches by month" note="Completed calls, emails, meetings and tasks">
          <div className={styles.crChart} role="img" aria-label={months.map((m) => `${m.label}: ${m.n}`).join("; ")}>
            {months.map((m) => (
              <div key={m.label} className={styles.crChartCol}>
                <div className={styles.crChartBars}>
                  <span className={`${styles.crChartBar} ${styles.crBarA}`} style={{ height: m.n ? `${Math.max((m.n / mx) * 100, 6)}%` : "0" }} title={`${m.n}`} />
                </div>
                <span className={styles.crChartLabel}>{m.label}</span>
              </div>
            ))}
          </div>
        </DCard>
        <DCard title="By channel" note={done.length ? `${done.length} completed ${done.length === 1 ? "touch" : "touches"}` : undefined}>
          {done.length === 0 && <p className={styles.crHint}>No completed activity yet.</p>}
          {done.length > 0 && (
            <>
              <div className={styles.crSplit} role="img" aria-label={byType.map((x) => `${x.t} ${x.share}%`).join(", ")}>
                {byType.filter((x) => x.n > 0).map((x) => <span key={x.t} style={{ width: `${x.share}%`, background: CH_COLOR[x.t] }} title={`${x.t} ${x.share}%`} />)}
              </div>
              <ul className={styles.crChan}>
                {byType.map((x) => (
                  <li key={x.t} className={x.n ? undefined : styles.crChanOff}>
                    <span className={styles.crChanIcon} style={{ background: `${CH_COLOR[x.t]}1f`, color: CH_COLOR[x.t] }}>{ACT_ICON[x.t]}</span>
                    <div className={styles.crChanMain}>
                      <strong>{x.t}</strong>
                      <span className={styles.crHint}>{x.n ? `${x.n} ${x.n === 1 ? "touch" : "touches"} · last ${fdate(x.last)}` : "No touches yet"}</span>
                    </div>
                    <span className={styles.crChanPct} style={{ color: CH_COLOR[x.t] }}>{x.share}%</span>
                  </li>
                ))}
              </ul>
            </>
          )}
        </DCard>
        <DCard title="AI insights" wide>
          <ul className={styles.crInsights}>
            <li><Sparkle20Regular />{acct ? accountSummary(acct, data) : "Add this company as an account to unlock deal insights."}</li>
            <li><Sparkle20Regular />{openOpps[0] ? nextAction(openOpps[0], data) : "No open deals. Consider creating an opportunity."}</li>
            <li><Sparkle20Regular />Preferred channel is {String(profile.channel).toLowerCase()}. {profile.dnEmail === "Yes" ? "Do not email is set." : "Email is allowed."}</li>
          </ul>
        </DCard>
      </div>
    </>
  );

  const F = ({ k, label, type, full }: { k: string; label: string; type?: "text" | "date"; full?: boolean }): React.ReactElement => (
    <div className={full ? styles.crFormFull : undefined}>
      <Field label={label}><Input type={type ?? "text"} value={String(profile[k] ?? "")} onChange={(_, dd) => set(k, dd.value)} /></Field>
    </div>
  );
  const S = ({ k, label, options }: { k: string; label: string; options: string[] }): React.ReactElement => (
    <div>
      <Field label={label}>
        <Select value={String(profile[k] ?? "")} onChange={(_, dd) => set(k, dd.value)}>{options.map((o) => <option key={o}>{o}</option>)}</Select>
      </Field>
    </div>
  );
  const details = (
    <div className={styles.crDtGrid}>
      <DCard title="Name and job"><div className={styles.crForm}>{F({ k: "first", label: "First name" })}{F({ k: "middle", label: "Middle name" })}{F({ k: "last", label: "Last name" })}{F({ k: "title", label: "Job title" })}{F({ k: "dept", label: "Department" })}</div></DCard>
      <DCard title="Contact"><div className={styles.crForm}>{F({ k: "email", label: "Email" })}{F({ k: "phone", label: "Phone" })}{F({ k: "mobile", label: "Mobile" })}{F({ k: "fax", label: "Fax" })}</div></DCard>
      <DCard title="Ownership and preferences">
        <div className={styles.crForm}>
          {S({ k: "owner", label: "Owner", options: ["", ...teamOpts(data).map((o) => o.v)] })}
          {S({ k: "source", label: "Lead source", options: ["Referral", "Event", "Website", "Social", "Cold call"] })}
          {S({ k: "channel", label: "Preferred channel", options: ["Email", "Phone", "SMS", "Chat"] })}
          {S({ k: "status", label: "Status", options: ["Active", "Inactive"] })}
          {S({ k: "dnEmail", label: "Do not email", options: ["No", "Yes"] })}
          {S({ k: "dnPhone", label: "Do not call", options: ["No", "Yes"] })}
        </div>
      </DCard>
      <DCard title="Address"><div className={styles.crForm}>{F({ k: "address", label: "Street", full: true })}{F({ k: "city", label: "City" })}{F({ k: "state", label: "State" })}{F({ k: "country", label: "Country" })}{F({ k: "zip", label: "Postal code" })}</div></DCard>
      <DCard title="Qualification">
        <div className={styles.crForm}>
          {F({ k: "topic", label: "Topic", full: true })}{F({ k: "website", label: "Website" })}{F({ k: "estRevenue", label: "Est. revenue" })}
          {S({ k: "budgetStatus", label: "Budget", options: ["No committed budget", "May buy", "Can buy", "Will buy"] })}
          {S({ k: "need", label: "Need", options: ["Must have", "Should have", "Good to have", "No need"] })}
          {S({ k: "timeframe", label: "Purchase timeframe", options: ["Immediate", "This quarter", "Next quarter", "This year", "Unknown"] })}
          {S({ k: "process", label: "Purchase process", options: ["Individual", "Committee", "Unknown"] })}
          {S({ k: "decider", label: "Decision maker", options: ["Yes", "No"] })}
        </div>
      </DCard>
      <DCard title="Description"><Field label="Notes about this contact"><Input value={String(profile.desc ?? "")} onChange={(_, dd) => set("desc", dd.value)} /></Field></DCard>
      <div className={`${styles.crDtSave} ${styles.crWide}`}>
        {dirty && <span className={styles.crHint}>You have unsaved changes.</span>}
        <Button size="small" onClick={() => setEdit({})} disabled={!dirty}>Discard</Button>
        <Button size="small" className={styles.crPrimary} appearance="primary" onClick={saveEdits} disabled={!dirty}>Save changes</Button>
      </div>
    </div>
  );

  const accOppIds = opps.map((o) => o.id);
  const quotes = rows(data, "quotes").filter((q) => accOppIds.includes(String(q.opp)));
  const related = (
    <div className={styles.crDtGrid}>
      <DCard title="Opportunities" wide><MiniTable head={["Deal", "Amount", "Stage", "Close"]} empty="No opportunities." body={opps.map((o) => [String(o.name), money(num(o.amount)), <Pill key="p" text={String(o.stage)} />, fdate(String(o.close))])} /></DCard>
      <DCard title="Quotes"><MiniTable head={["Quote", "Total", "Status"]} empty="No quotes." body={quotes.map((q) => [q.id, money(quoteTotal(q, data)), <Pill key="p" text={quoteState(q)} />])} /></DCard>
      <DCard title="Orders and invoices"><MiniTable head={["Record", "Total", "Status"]} empty="No orders or invoices." body={[...rows(data, "orders").filter((o) => o.account === accId).map((o) => [o.id, money(num(o.total)), <Pill key="o" text={String(o.status)} />]), ...rows(data, "invoices").filter((i) => i.account === accId).map((i) => [i.id, money(num(i.total)), <Pill key="i" text={String(i.status)} />])]} /></DCard>
      <DCard title="Support cases"><MiniTable head={["Case", "Title", "Status"]} empty="No cases." body={rows(data, "cases").filter((c) => c.account === accId).map((c) => [c.id, String(c.title), <Pill key="c" text={String(c.status)} />])} /></DCard>
      <DCard title="Marketing lists"><MiniTable head={["List", "Members"]} empty="Not on any list." body={lists.map((l) => [String(l.name), num(l.members).toLocaleString("en-NG")])} /></DCard>
      <DCard title="Notes" wide><MiniTable head={["Date", "Note"]} empty="No notes." body={notes.map((n) => [fdate(String(n.date)), String(n.text)])} /></DCard>
    </div>
  );

  /* ---------- dialogs ---------- */
  const dialog = () => {
    if (!dlg) return null;
    const close = () => setDlg(null);
    const typeOpts = (...o: string[]) => opts(...o);
    if (dlg.kind === "add" || dlg.kind === "connect") {
      const connect = dlg.kind === "connect";
      return <RecordDialog title={connect ? "Connect with contact" : "Add new activity"} submit={connect ? "Schedule" : "Create"} data={data} onClose={close}
        fields={[{ key: "type", label: "Type", kind: "select", options: connect ? typeOpts("Call", "Email", "Meeting", "Chat") : typeOpts("Task", "Call", "Meeting", "Email"), required: true }, { key: "date", label: "Due date", kind: "date", required: true }, { key: "subject", label: "Subject", kind: "text", required: true, full: true }]}
        initial={{ type: connect ? "Call" : "Task", date: off(1) }}
        onSave={(v) => { addActivity(v, connect ? "Connect" : "Add new", "Open", `${String(v.type)} planned for ${fdate(String(v.date))}`); close(); return null; }} />;
    }
    if (dlg.kind === "list") {
      return <RecordDialog title="Add to marketing list" submit="Add" data={data} onClose={close}
        fields={[{ key: "list", label: "Marketing list", kind: "select", options: (d) => rows(d, "lists").filter((l) => l.status === "Active").map((l) => ({ v: l.id, l: String(l.name) })), required: true, full: true }]} initial={{}}
        onSave={(v) => {
          if (rows(data, "memberships").some((m) => m.contact === tab.id && m.list === v.list)) return `${name} is already on that list.`;
          setData((d) => audit({ ...d, memberships: [...rows(d, "memberships"), R(nextId("MB-", rows(d, "memberships")), { contact: tab.id, list: String(v.list) })], lists: rows(d, "lists").map((l) => (l.id === v.list ? ({ ...l, members: num(l.members) + 1 } as Row) : l)) }, "Marketing lists", String(v.list), "Member added", name));
          say(`${name} added to ${nameOf(data, "lists", v.list)}`); close(); return null;
        }} />;
    }
    if (dlg.kind === "stake") {
      return <RecordDialog title="Add stakeholder" submit="Add" data={data} onClose={close}
        fields={[{ key: "name", label: "Full name", kind: "text", required: true, full: true }, { key: "title", label: "Job title", kind: "text" }, { key: "email", label: "Email", kind: "text" }]} initial={{}}
        onSave={(v) => { if (!acct) return "Qualify the lead first so the company exists as an account."; setData((d) => audit({ ...d, contacts: [...rows(d, "contacts"), R(nextId("CT-", rows(d, "contacts")), { name: String(v.name), title: String(v.title), account: acct.id, email: String(v.email), phone: "", status: "Active" })] }, "Contacts", tab.id, "Stakeholder added", String(v.name))); say(`${String(v.name)} added as a stakeholder`); close(); return null; }} />;
    }
    if (dlg.kind === "comp") {
      return <RecordDialog title="Add competitor" submit="Add" data={data} onClose={close}
        fields={[{ key: "competitor", label: "Competitor", kind: "select", options: (d) => rows(d, "competitors").map((c) => ({ v: c.id, l: String(c.name) })), required: true, full: true }]} initial={{}}
        onSave={(v) => { if (rows(data, "competitorLinks").some((l) => l.contact === tab.id && l.competitor === v.competitor)) return "Already added."; setData((d) => audit({ ...d, competitorLinks: [...rows(d, "competitorLinks"), R(nextId("CL-", rows(d, "competitorLinks")), { contact: tab.id, competitor: String(v.competitor) })] }, "Contacts", tab.id, "Competitor added", nameOf(data, "competitors", v.competitor))); say("Competitor added"); close(); return null; }} />;
    }
    if (dlg.kind === "prod") {
      return <RecordDialog title="Add product of interest" submit="Add" data={data} onClose={close}
        fields={[{ key: "product", label: "Product", kind: "select", options: productOpts, required: true, full: true }]} initial={{}}
        onSave={(v) => { if (rows(data, "interests").some((l) => l.contact === tab.id && l.product === v.product)) return "Already added."; setData((d) => audit({ ...d, interests: [...rows(d, "interests"), R(nextId("PI-", rows(d, "interests")), { contact: tab.id, product: String(v.product) })] }, "Contacts", tab.id, "Product added", nameOf(data, "products", v.product))); say("Product added"); close(); return null; }} />;
    }
    if (dlg.kind === "assign") {
      return <RecordDialog title="Assign contact" submit="Assign" data={data} onClose={close}
        fields={[{ key: "owner", label: "New owner", kind: "select", options: teamOpts, required: true, full: true }]} initial={{ owner: String(profile.owner) }}
        onSave={(v) => { upsert({ owner: String(v.owner) }, "Assigned", `${name} assigned to ${String(v.owner)}`); close(); return null; }} />;
    }
    if (dlg.kind === "resched" && dlg.ref) {
      const a = dlg.ref;
      return <RecordDialog title="Reschedule activity" submit="Reschedule" data={data} onClose={close}
        fields={[{ key: "date", label: "New due date", kind: "date", required: true }]} initial={{ date: String(a.date) < off(0) ? off(1) : String(a.date) }}
        onSave={(v) => { patchActivity(a.id, { date: String(v.date) }, "Reschedule", `${String(a.subject)} moved to ${fdate(String(v.date))}`); close(); return null; }} />;
    }
    const sms = dlg.kind === "sms";
    return <RecordDialog title={sms ? "Send SMS" : "Log chat"} submit={sms ? "Send" : "Log"} data={data} onClose={close}
      fields={[{ key: "subject", label: sms ? "Message" : "Chat summary", kind: "text", required: true, full: true }]} initial={{}}
      onSave={(v) => { addActivity({ type: sms ? "SMS" : "Chat", subject: `${sms ? "SMS" : "Chat"}: ${String(v.subject)}`, date: off(0) }, sms ? "SMS sent" : "Chat logged", "Completed", sms ? `SMS sent to ${name}` : "Chat logged"); close(); return null; }} />;
  };

  const pill = (icon: React.ReactElement, label: string, onClick: () => void, key?: string) => (
    <Button key={key ?? label} size="small" shape="circular" icon={icon} className={styles.crTbPill} onClick={onClick}>{label}</Button>
  );

  return (
    <div className={styles.crDt}>
      <div className={`${styles.buttonContainer} ${styles.mHDs1} ${styles.crTbBar}`} role="toolbar" aria-label="Contact actions">
        <Button size="small" icon={<ChevronLeft16Regular />} className={styles.scrollButton} onClick={bar.onLeft} disabled={!bar.canLeft} aria-label="Scroll actions left" />
        <div className={styles.sliderTrack} ref={bar.trackRef}>
          <Button size="small" shape="circular" icon={<Save16Regular />} className={`${styles.crTbPill} ${dirty ? styles.crTbPillOn : ""}`} onClick={saveEdits}>Save</Button>
          {pill(<Add16Regular />, "Add New", () => setDlg({ kind: "add" }))}
          {pill(<BookDismiss16Regular />, active ? "Deactivate" : "Activate", () => upsert({ status: active ? "Inactive" : "Active" }, active ? "Deactivated" : "Activated", `${name} ${active ? "deactivated" : "activated"}`))}
          {pill(<PeopleAdd16Regular />, "Connect", () => setDlg({ kind: "connect" }))}
          {pill(<FolderList16Regular />, "Add to marketing list", () => setDlg({ kind: "list" }))}
          {pill(<PersonAdd16Regular />, "Assign", () => setDlg({ kind: "assign" }))}
          {pill(<Delete16Regular />, "Delete", () => setConfirmDel(true))}
          <Menu>
            <MenuTrigger disableButtonEnhancement>
              <MenuButton className={styles.crTbPill} icon={<Share16Regular />} size="small" shape="circular">Share</MenuButton>
            </MenuTrigger>
            <MenuPopover>
              <MenuList>
                <MenuItem onClick={copyDetails}>Copy contact details</MenuItem>
                <MenuItem onClick={downloadVcard}>Download vCard</MenuItem>
                <MenuItem onClick={mail}>Email summary</MenuItem>
              </MenuList>
            </MenuPopover>
          </Menu>
        </div>
        <Button size="small" icon={<ChevronRight16Regular />} className={styles.scrollButton} onClick={bar.onRight} disabled={!bar.canRight} aria-label="Scroll actions right" />
      </div>

      <div className={styles.crDtHead}>{persona}</div>

      <div className={styles.crSeg} role="tablist" aria-label="Contact sections">
        {SUBTABS.map((t) => (
          <button key={t} type="button" role="tab" aria-selected={sub === t} className={`${styles.crSegBtn} ${sub === t ? styles.crSegOn : ""}`} onClick={() => setSub(t)}>{t}</button>
        ))}
        {!active && <Pill text="Inactive" />}
        {dirty && <span className={styles.crHint}>Unsaved changes</span>}
      </div>

      <div className={styles.crDtBody}>
        {sub === "Summary" && summary}
        {sub === "Analytics" && analytics}
        {sub === "Details" && details}
        {sub === "Related" && related}
      </div>

      {dialog()}
      <Dialog open={!!draftMail} onOpenChange={(_, dd) => !dd.open && setDraftMail(null)}>
        <DialogSurface className={styles.crDialog}>
          <DialogBody>
            <DialogTitle action={<DialogTrigger action="close"><Button size="small" appearance="subtle" aria-label="Close" icon={<Dismiss24Regular />} /></DialogTrigger>}><span className={styles.crAiHead}><Sparkle20Regular /> {draftMail?.title}</span></DialogTitle>
            <DialogContent><pre className={styles.crDraft}>{draftMail?.body}</pre><p className={styles.crHint}>AI-written draft. Review it before sending.</p></DialogContent>
            <DialogActions>
              <Button size="small" icon={<Copy20Regular />} onClick={async () => { try { await navigator.clipboard.writeText(draftMail?.body ?? ""); say("Draft copied"); } catch { say("Copy isn't available in this browser", true); } }}>Copy</Button>
              <Button size="small" className={styles.crPrimary} appearance="primary" onClick={() => { if (draftMail) addActivity({ type: "Email", subject: draftMail.title, date: off(0) }, "Email logged", "Completed", "Email logged as a completed activity"); setDraftMail(null); }}>Log as sent email</Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>
      <Dialog open={confirmDel} onOpenChange={(_, dd) => !dd.open && setConfirmDel(false)}>
        <DialogSurface className={styles.crDialogSm}>
          <DialogBody>
            <DialogTitle>Delete contact?</DialogTitle>
            <DialogContent>{name} will be removed from your work flow. This can't be undone.</DialogContent>
            <DialogActions>
              <DialogTrigger action="close"><Button size="small">Cancel</Button></DialogTrigger>
              <Button size="small" className={styles.crDanger} appearance="primary" onClick={() => { setConfirmDel(false); setData((d) => audit(d, "Contacts", tab.id, "Deleted", name)); onRemove(tab.id); say(`${name} deleted`); }}>Delete</Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>
    </div>
  );
};

const CRM: React.FC = () => {
  const [isContactFormVisible, setIsContactFormVisible] = useState<boolean>(false);
  const [activeTab, setActiveTab] = useState<string>("Dashboard");
  const be = useCrmBackend();
  const wantLive = !!be && haveToken();
  const sayRef = useRef<(msg: string, err?: boolean) => void>(() => undefined);
  // the CRM client with calendar updates added: every activity change also lands on the person's calendar
  const liveCrm = useMemo(() => (be?.crm ? withCalendar(be.crm, (m) => sayRef.current(m, true)) : undefined), [be?.crm]); // eslint-disable-line react-hooks/exhaustive-deps
  const liveSubscribe = be?.subscribe;
  const resyncTick = be?.resyncTick ?? 0;
  const [crmData, setCrmData] = useState<Data>(emptyData);
  const workflowTabs = useMemo(() => tabsOf(crmData), [crmData]);
  const [liveStatus, setLiveStatus] = useState<"idle" | "loading" | "ready" | "error">("idle");
  const [liveError, setLiveError] = useState<string | null>(null);
  const [retry, setRetry] = useState(0);
  useEffect(() => { if (!wantLive) { setCrmData(emptyData()); setLiveStatus("idle"); } }, [wantLive]);
  const [explorerKey, setExplorerKey] = useState<string | undefined>(undefined);
  const blanked = useRef(false);
  const [removed, setRemoved] = useState<string[]>([]);
  const [toast, setToast] = useState<{ msg: string; err: boolean } | null>(null);
  useEffect(() => {
    if (!toast) return;
    const tm = window.setTimeout(() => setToast(null), 3500);
    return () => window.clearTimeout(tm);
  }, [toast]);
  const say = (msg: string, err = false) => setToast({ msg, err });
  sayRef.current = say;
  const reloadLive = useCallback(async (srcs: string[] = LIVE_SRCS) => {
    if (!liveCrm) return;
    const got = await Promise.all(srcs.map(async (s) => [s, await liveCrm.listAll(s)] as const));
    setCrmData((d) => { const n = { ...d }; got.forEach(([s, r]) => { n[s] = r; }); return n; });
  }, [liveCrm]);
  // load from the services (and again after a lost connection came back, or when Retry is pressed)
  useEffect(() => {
    if (!wantLive || !liveCrm) return;
    let alive = true;
    if (!blanked.current) { blanked.current = true; setCrmData(emptyData()); }
    setLiveStatus((s) => (s === "ready" ? s : "loading"));
    setLiveError(null);
    reloadLive().then(() => { if (alive) setLiveStatus("ready"); }).catch((e) => { if (alive) { setLiveStatus((s) => (s === "ready" ? s : "error")); setLiveError(apiMsg(e)); } });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wantLive, liveCrm, resyncTick, retry, reloadLive]);
  // live changes made by other people: fetch the changed row again
  useEffect(() => {
    if (!wantLive || !liveCrm || !liveSubscribe) return;
    return liveSubscribe(async (c) => {
      const src = sourceOf(c);
      if (!src) return;
      if (c.action === "Deleted") { setCrmData((d) => ({ ...d, [src]: rows(d, src).filter((r) => r.id !== c.id) })); return; }
      try { const row = await liveCrm.get(src, c.id); setCrmData((d) => upsertRow(d, src, row)); } catch { /* not readable by this person, or already gone */ }
    });
  }, [wantLive, liveCrm, liveSubscribe]);
  const live = React.useMemo<LiveCtx | undefined>(() => (wantLive && liveCrm && liveStatus === "ready"
    ? {
      crm: liveCrm,
      team: rows(crmData, "team"),
      put: (s, r) => setCrmData((d) => upsertRow(d, s, r)),
      drop: (s, id) => setCrmData((d) => ({ ...d, [s]: rows(d, s).filter((r) => r.id !== id) })),
      reload: async (srcs) => { try { await reloadLive(srcs); } catch (e) { say(apiMsg(e), true); } },
    }
    : undefined), [wantLive, liveCrm, liveStatus, crmData, reloadLive]); // eslint-disable-line react-hooks/exhaustive-deps
  const openExplorer = (key: string) => { setExplorerKey(key); setActiveTab("All records"); };
  const [activeWorkFlow, setActiveWorkFlow] = useState<string>("");
  const [searchQuery, setSearchQuery] = useState('');
  const [alwaysShow, setAlwaysShow] = useState<boolean>(true);
  const [selectedWorkflow, setSelectedWorkflow] = useState<WorkflowTab | null>(null);
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [selectedUserDetails, setSelectedUserDetails] = useState<string>("");
  const { user } = useAuth(); // Extract `refreshLogin`
  const scrollRef = React.useRef<HTMLDivElement>(null);
  const [authToken, setAuthToken] = useState<string | null>(localStorage.getItem('authToken'));
  const scrollRefH = useRef<HTMLDivElement>(null);
  const [canScrollLeftH, setCanScrollLeftH] = useState(false);
  const [canScrollRightH, setCanScrollRightH] = useState(false);





  const scrollLeft = () => {
    scrollRef.current?.scrollBy({ left: -120, behavior: "smooth" });
  };
  const scrollRight = () => {
    scrollRef.current?.scrollBy({ left: 120, behavior: "smooth" });
  };


  const handleClick = (tabValue: string) => {
    setActiveTab(tabValue); // Update to the newly clicked tab
  };

  const handleSelectWorkFlow = (selectw: any, userid: any) => {
    setActiveWorkFlow(selectw);
    console.log("Contact Form Closed: ", isContactFormVisible);
    // Optional: show loading indicator here
    const userDetails = null;//await fetchUserDetails(id); // Your API or lookup function
    setSelectedUserDetails("1");

    console.log("Selected User Details: ", selectedUserDetails);
  }


  const handelContactForm = () => {

    setIsContactFormVisible(true);
    console.log("Contact Form Opened: ", isContactFormVisible);
  }




  const handleCotactClosed = () => {
    setIsContactFormVisible(false);

  }

  const groupedTabs = workflowTabs.filter((w) => !removed.includes(w.id)).reduce<Record<string, WorkflowTab[]>>((acc, tab) => {
    const date = parseISO(tab.datetime);
    const groupLabel = isToday(date)
      ? 'Today'
      : format(date, 'MMMM d');

    if (!acc[groupLabel]) {
      acc[groupLabel] = [];
    }
    acc[groupLabel].push(tab);
    return acc;
  }, {} as Record<string, WorkflowTab[]>);


  const sortedGroupedTabs = Object.entries(groupedTabs)
    .sort(([dateA], [dateB]) => {
      const parsedA = parse(dateA, 'MMMM d', new Date());
      const parsedB = parse(dateB, 'MMMM d', new Date());
      return parsedB.getTime() - parsedA.getTime(); // recent first
    });


  const maxChars = 40; // customize as needed
  const originalText = "UBA Groups new product launch invitation";

  const limitedText =
    originalText.length > maxChars
      ? originalText.slice(0, maxChars) + "..."
      : originalText;

  const fetchCotacts = async (token: any, ev: any) => {
    try {
      const arrivalsData = await apiCall({
        service: 'crm',
        endpoint: `hotels/search?query=${ev}`,
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (arrivalsData.error) {

      }

      return arrivalsData;
    } catch (error) {
      console.error('Error fetching arrivals:', error);

      return null;
    }
  };

  // Example useEffect to demonstrate component mount behavior
  // Cubic Bézier easing function (implements the curve calculation)
  const cubicBezier = (x1: any, y1: any, x2: any, y2: any) => {
    return (t: any) => {
      if (t === 0 || t === 1) return t;
      let start = 0;
      let end = 1;
      while (end - start > 0.001) {
        const mid = (start + end) / 2;
        const x = 3 * mid * (1 - mid) * (1 - mid) * x1 + 3 * mid * mid * (1 - mid) * x2 + mid * mid * mid;
        if (x >= t) end = mid;
        else start = mid;
      }
      return 3 * end * (1 - end) * (1 - end) * y1 + 3 * end * end * (1 - end) * y2 + end * end * end;
    };
  };

  // Use ease-in-out curve: cubic-bezier(0.42, 0, 0.58, 1)
  // Adjust these values for different feels (e.g., 0.25, 0.1, 0.25, 1 for standard ease-in-out)
  const easing = cubicBezier(0.42, 0, 0.58, 1);

  const animateScroll = (element: any, delta: any, duration = 300) => {
    if (!element) return;
    const start = element.scrollLeft;
    const target = start + delta;
    const startTime = performance.now();

    const animate = (currentTime: any) => {
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const easedProgress = easing(progress);
      element.scrollLeft = start + delta * easedProgress;

      if (progress < 1) {
        requestAnimationFrame(animate);
      } else {
        updateScrollButtons(); // Ensure buttons update after animation ends
      }
    };

    requestAnimationFrame(animate);
  };

  const scrollLeftH = () => {
    animateScroll(scrollRefH.current, -200); // Scroll left by 200px
  };

  const scrollRightH = () => {
    animateScroll(scrollRefH.current, 200); // Scroll right by 200px
  };

 const updateScrollButtons = () => {
  const el = scrollRefH.current;
  if (el) {
    const { scrollLeft, scrollWidth, clientWidth } = el;
    console.log('Scroll check:', { scrollLeft, scrollWidth, clientWidth });
    setCanScrollLeftH(scrollLeft > 0);
    setCanScrollRightH(scrollLeft + clientWidth < scrollWidth - 1);
  }
};

  useEffect(() => {
    const el = scrollRefH.current;
    if (!el) return;
    updateScrollButtons();
    el.addEventListener("scroll", updateScrollButtons);
    window.addEventListener("resize", updateScrollButtons);
    return () => {
      el.removeEventListener("scroll", updateScrollButtons);
      window.removeEventListener("resize", updateScrollButtons);
    };
  }, [selectedUserDetails, activeWorkFlow]);

  const currentTab = workflowTabs.find((w) => w.id === activeWorkFlow && !removed.includes(w.id));

  return (
    <>
      {/* <TaskList message={message} user={user} /> */}
      <div className={styles.gU1Tf}>
        <div className={styles.jUsfu}>
          <div className={styles.QIl4y}>
            <div className={`${styles.O0VOp1}`}>
              <div className={`${styles.VvU3MOvL}`}>
                <div className={`${styles.clieTWU2V}`}>
                  <div  className={`${styles.clieTWU2Vc}`}>
                    <Button shape="circular" size="small" className={`${styles.vgnei} ${styles.cgTactive}`}>
                      <span className={`${styles.ci_menu_toggle} ${styles.border0} ${styles.hasImage}`}>
                        <img src={crm} alt="CRM" height={22} width={22} />
                      </span>
                      <span className={`${styles.ci_menu_brand} ${styles.dflex} ${styles.pe0}`}>CRM</span>
                    </Button>
                    <Button shape="circular" size="small" className={`${styles.Tab6re}`}>
                      <Menu>
                        <MenuTrigger disableButtonEnhancement>
                          <Button className={`${styles.Tab6re}`} icon={<Timer16Regular />}>Recent
                            <ChevronDown16Regular />
                          </Button>
                        </MenuTrigger>

                        <MenuPopover>
                          <MenuList>
                            <MenuItem>My Channel </MenuItem>
                            <MenuItem>My Actions</MenuItem>
                            <MenuItem>Teams</MenuItem>
                            <MenuItem>Customers</MenuItem>
                          </MenuList>
                        </MenuPopover>
                      </Menu>
                    </Button>
                    <Button size="small" shape="circular" className={`${styles.JKLow}`}>
                      <Menu>
                        <MenuTrigger disableButtonEnhancement>
                          <Button className={`${styles.JKLow}`} icon={<Pin16Regular />}>Pinned
                            <ChevronDown16Regular />
                          </Button>

                        </MenuTrigger>

                        <MenuPopover>
                          <MenuList>
                            <MenuItem>Forcast </MenuItem>
                            <MenuItem>Channel</MenuItem>
                            <MenuItem>Leads</MenuItem>
                            <MenuItem>Actions</MenuItem>
                          </MenuList>
                        </MenuPopover>
                      </Menu>
                    </Button>

                  </div>
                  <div  className={`${styles.clieTWU2Vc}`}>
                    <Button shape="circular" size="small"
                      className={`${styles.narTIl}`}>
                      <Menu>
                        <MenuTrigger disableButtonEnhancement>
                          <MenuButton
                            icon={<Settings16Regular />}
                            className={`${styles.narTIl}`}
                            menuIcon={<ChevronDown16Regular />}
                          >Actions</MenuButton>
                        </MenuTrigger>
                        <MenuPopover>
                          <MenuList>
                            <MenuItem icon={<ArrowDownloadRegular />}>Import Records </MenuItem>
                            <MenuItem icon={<DocumentArrowDownRegular />}>Spreadsheets</MenuItem>
                            <MenuItem icon={<DocumentFlowchartRegular />}>Insert View in article</MenuItem>
                            <MenuItem icon={<LinkRegular />}>Insert Link in article</MenuItem>
                            <MenuItem icon={<DocumentDataLinkRegular />}>Link Menu in spread sheet</MenuItem>
                          </MenuList>
                        </MenuPopover>
                      </Menu>
                    </Button>
                  </div>
                </div>
                <div className={styles.BHt3}>
                  <div className={styles.UIy67b}>
                    <div className={styles.r6YRf3}>
                      <List selectionMode="single" navigationMode="composite" className={`${styles.ERFwq} ${styles.customScrollBar}`}>
                      <h2 className={styles.YCFowe}>
                        My Work
                      </h2>
                      <ListItem className={styles.YCFSra} checkmark={null}>
                        <TabList defaultSelectedValue="" vertical>
                          <Tab
                            value="Sales accelerator"
                            onClick={() => handleClick("Sales accelerator")}
                            className={`${styles.Yuisvt} ${activeTab === "Sales accelerator" ? styles.cgTactive : ""}`}
                            icon={<TopSpeedRegular />}
                          >
                            Sales accelerator
                          </Tab>
                          <Tab
                            value="Activity"
                            onClick={() => handleClick("Activity")}
                            className={`${styles.Yuisvt} ${activeTab === "Activity" ? styles.cgTactive : ""}`}
                            icon={<NoteEditRegular />}
                          >
                            Activity
                          </Tab>
                          <Tab
                            value="Dashboard"
                            onClick={() => handleClick("Dashboard")}
                            className={`${styles.Yuisvt} ${activeTab === "Dashboard" ? styles.cgTactive : ""}`}
                            icon={<AppTitleRegular />}
                          >
                            Dashboard
                          </Tab>
                          <Tab
                            value="AI assistant"
                            onClick={() => handleClick("AI assistant")}
                            className={`${styles.Yuisvt} ${activeTab === "AI assistant" ? styles.cgTactive : ""}`}
                            icon={<SparkleRegular />}
                          >
                            AI assistant
                          </Tab>
                        </TabList>
                      </ListItem>
                      <h2 className={styles.YCFowe}>
                        Customer
                      </h2>
                      <ListItem className={styles.YCFSra} checkmark={null}>
                        <TabList defaultSelectedValue="" vertical>
                          <Tab
                            value="Accounts"
                            onClick={() => handleClick("Accounts")}
                            className={`${styles.Yuisvt} ${activeTab === "Accounts" ? styles.cgTactive : ""}`}
                            icon={<PersonAccountsRegular />}
                          >
                            Accounts
                          </Tab>
                          <Tab
                            value="Linked Contact"
                            onClick={() => handleClick("Linked Contact")}
                            className={`${styles.Yuisvt} ${activeTab === "Linked Contact" ? styles.cgTactive : ""}`}
                            icon={<ContactCardLinkRegular />}
                          >
                            Linked Contact
                          </Tab>
                        </TabList>
                      </ListItem>
                      <h2 className={styles.YCFowe}>
                        Sales
                      </h2>
                      <ListItem className={styles.YCFSra} checkmark={null}>
                        <TabList defaultSelectedValue="" vertical>
                          <Tab
                            value="Leads"
                            onClick={() => handleClick("Leads")}
                            className={`${styles.Yuisvt} ${activeTab === "Leads" ? styles.cgTactive : ""}`}
                            icon={<SlideTextCallRegular />}
                          >
                            Leads
                          </Tab>
                          <Tab
                            value="Opportunity"
                            onClick={() => handleClick("Opportunity")}
                            className={`${styles.Yuisvt} ${activeTab === "Opportunity" ? styles.cgTactive : ""}`}
                            icon={<NoteAddRegular />}
                          >
                            Opportunity
                          </Tab>
                          <Tab
                            value="Pipeline"
                            onClick={() => handleClick("Pipeline")}
                            className={`${styles.Yuisvt} ${activeTab === "Pipeline" ? styles.cgTactive : ""}`}
                            icon={<DataFunnelRegular />}
                          >
                            Pipeline
                          </Tab>
                          <Tab
                            value="Forecast"
                            onClick={() => handleClick("Forecast")}
                            className={`${styles.Yuisvt} ${activeTab === "Forecast" ? styles.cgTactive : ""}`}
                            icon={<ChartMultipleRegular />}
                          >
                            Forecast
                          </Tab>
                          <Tab
                            value="Compatitors"
                            onClick={() => handleClick("Compatitors")}
                            className={`${styles.Yuisvt} ${activeTab === "Compatitors" ? styles.cgTactive : ""}`}
                            icon={<FolderPeopleRegular />}
                          >
                            Compatitors
                          </Tab>
                        </TabList>
                      </ListItem>
                      <h2 className={styles.YCFowe}>
                        collateral
                      </h2>
                      <ListItem className={styles.YCFSra} checkmark={null}>
                        <TabList defaultSelectedValue="" vertical>
                          <Tab
                            value="Quotes"
                            onClick={() => handleClick("Quotes")}
                            className={`${styles.Yuisvt} ${activeTab === "Quotes" ? styles.cgTactive : ""}`}
                            icon={<SlideTextCallRegular />}
                          >
                            Quotes
                          </Tab>
                          <Tab
                            value="Orders"
                            onClick={() => handleClick("Orders")}
                            className={`${styles.Yuisvt} ${activeTab === "Orders" ? styles.cgTactive : ""}`}
                            icon={<NoteAddRegular />}
                          >
                            Orders
                          </Tab>
                          <Tab
                            value="Linked Invoices"
                            onClick={() => handleClick("Linked Invoices")}
                            className={`${styles.Yuisvt} ${activeTab === "Linked Invoices" ? styles.cgTactive : ""}`}
                            icon={<FolderPeopleRegular />}
                          >
                            Linked Invoices
                          </Tab>
                          <Tab
                            value="Products"
                            onClick={() => handleClick("Products")}
                            className={`${styles.Yuisvt} ${activeTab === "Products" ? styles.cgTactive : ""}`}
                            icon={<FolderPeopleRegular />}
                          >
                            Products
                          </Tab>
                        </TabList>
                      </ListItem>
                      <h2 className={styles.YCFowe}>
                        Marketing
                      </h2>
                      <ListItem className={styles.YCFSra} checkmark={null}>
                        <TabList defaultSelectedValue="" vertical>
                          <Tab
                            value="Campaigns"
                            onClick={() => handleClick("Campaigns")}
                            className={`${styles.Yuisvt} ${activeTab === "Campaigns" ? styles.cgTactive : ""}`}
                            icon={<MegaphoneRegular />}
                          >
                            Campaigns
                          </Tab>
                          <Tab
                            value="Marketing lists"
                            onClick={() => handleClick("Marketing lists")}
                            className={`${styles.Yuisvt} ${activeTab === "Marketing lists" ? styles.cgTactive : ""}`}
                            icon={<PeopleListRegular />}
                          >
                            Marketing lists
                          </Tab>
                        </TabList>
                      </ListItem>
                      <h2 className={styles.YCFowe}>
                        Service
                      </h2>
                      <ListItem className={styles.YCFSra} checkmark={null}>
                        <TabList defaultSelectedValue="" vertical>
                          <Tab
                            value="Cases"
                            onClick={() => handleClick("Cases")}
                            className={`${styles.Yuisvt} ${activeTab === "Cases" ? styles.cgTactive : ""}`}
                            icon={<HeadsetRegular />}
                          >
                            Cases
                          </Tab>
                          <Tab
                            value="Knowledge base"
                            onClick={() => handleClick("Knowledge base")}
                            className={`${styles.Yuisvt} ${activeTab === "Knowledge base" ? styles.cgTactive : ""}`}
                            icon={<BookRegular />}
                          >
                            Knowledge base
                          </Tab>
                        </TabList>
                      </ListItem>
                      {MOD_GROUPS.map((g) => (
                        <React.Fragment key={g.group}>
                          <h2 className={styles.YCFowe}>
                            {g.group}
                          </h2>
                          <ListItem className={styles.YCFSra} checkmark={null}>
                            <TabList defaultSelectedValue="" vertical>
                              {g.ids.map((id) => MOD_PAGES.find((m) => m.id === id)).filter((m): m is ModPage => !!m).map((m) => (
                                <Tab
                                  key={m.id}
                                  value={m.id}
                                  onClick={() => handleClick(m.id)}
                                  className={`${styles.Yuisvt} ${activeTab === m.id ? styles.cgTactive : ""}`}
                                  icon={m.icon}
                                >
                                  {m.title}
                                </Tab>
                              ))}
                            </TabList>
                          </ListItem>
                        </React.Fragment>
                      ))}
                      <h2 className={styles.YCFowe}>
                        Admin
                      </h2>
                      <ListItem className={styles.YCFSra} checkmark={null}>
                        <TabList defaultSelectedValue="" vertical>
                          <Tab
                            value="Sales team"
                            onClick={() => handleClick("Sales team")}
                            className={`${styles.Yuisvt} ${activeTab === "Sales team" ? styles.cgTactive : ""}`}
                            icon={<PeopleTeamRegular />}
                          >
                            Sales team
                          </Tab>
                          <Tab
                            value="Activity log"
                            onClick={() => handleClick("Activity log")}
                            className={`${styles.Yuisvt} ${activeTab === "Activity log" ? styles.cgTactive : ""}`}
                            icon={<HistoryRegular />}
                          >
                            Activity log
                          </Tab>
                          <Tab
                            value="All records"
                            onClick={() => handleClick("All records")}
                            className={`${styles.Yuisvt} ${activeTab === "All records" ? styles.cgTactive : ""}`}
                            icon={<PeopleListRegular />}
                          >
                            All records
                          </Tab>
                        </TabList>
                      </ListItem>
                    </List>
                    </div>
                  </div>
                 
                    {activeTab === "Sales accelerator" && (
                       <div className={styles.YtRw}>
                      <>
                        <div className={styles.WRe37}>
                          <div className={styles.DVEYbe}>
                            <div className={styles.HDs1}>
                              <span className={styles.hmect}>My work flow</span>

                              <Menu positioning={{ autoSize: true }}>
                                <MenuTrigger disableButtonEnhancement>
                                  <Button shape="circular" size="small" className={styles.MorE3} icon={<MoreHorizontalRegular />}></Button>
                                </MenuTrigger>

                                <MenuPopover>
                                  <MenuList>
                                    <MenuItem>New </MenuItem>
                                    <MenuItem>New Window</MenuItem>
                                    <MenuItem disabled>Open File</MenuItem>
                                    <MenuItem>Open Folder</MenuItem>
                                  </MenuList>
                                </MenuPopover>
                              </Menu>
                            </div>
                            <div className={styles.SRCcont}>
                              <SearchBox
                                className={styles.Fiwb}
                                placeholder="Search work flow"
                                appearance="filled-darker"
                                value={searchQuery}
                                onChange={(_, data) => setSearchQuery(data.value)}
                              />
                            </div>
                            <div className={styles.LOWqs}></div>
                            <div className={`${styles.LOWqs} ${styles.ILWY6g}`}>
                              <TabList defaultSelectedValue="" className={`${styles.Jsrv} ${styles.LOWqs} ${styles.customScrollBar}`} vertical>
                                {sortedGroupedTabs.map(([dateGroup, tabs], index) => {
                                  const filteredTabs = tabs.filter((tab) =>
                                    [tab.id, tab.name, tab.role, tab.company]
                                      .some((field) => field.toLowerCase().includes(searchQuery.toLowerCase()))
                                  );

                                  if (filteredTabs.length === 0) return null;

                                  return (
                                    <div key={dateGroup}>
                                      {/* Show header and count */}
                                      <div className={styles.HJne5}>
                                        <div className={styles.Y5rV}>{dateGroup}</div>
                                        <div className={styles.Y5rYs}>{filteredTabs.length}</div>
                                      </div>

                                      {filteredTabs.map((tab) => {
                                        const timeString = format(parseISO(tab.datetime), 'h:mm a');

                                        return (
                                          <Tab
                                            key={tab.id}
                                            value={tab.id}
                                            onClick={() => handleSelectWorkFlow(tab.id, "1")}
                                            className={`${styles.Mvtuisvt} ${activeWorkFlow === tab.id ? styles.cgTactive : ""}`}
                                          >
                                            <div className={styles.Cgscf}>
                                              <span className={styles.Wrye}>
                                                <Persona
                                                  name={tab.name}
                                                  secondaryText={
                                                    <span style={{ fontWeight: 500 }}>
                                                      {tab.role} <span style={{ fontSize: '1.2em', fontWeight: 'bold' }}>•</span> {tab.company}
                                                    </span>
                                                  }
                                                  avatar={
                                                    {
                                                      image: { src: tab.avatar },
                                                      color: "colorful"
                                                    }}
                                                />
                                              </span>

                                              <span className={styles.NUWV6w}>{timeString}</span>
                                            </div>
                                          </Tab>
                                        );
                                      })}
                                    </div>
                                  );
                                })}
                              </TabList>
                            </div>
                          </div>

                        </div>
                        {selectedUserDetails && currentTab && (
                          <div className={`${styles.Lbwe} ${styles.customScrollBar}`}>
                            <div className={styles.OTGceu}>
                              <ContactPane
                                tab={currentTab}
                                data={crmData}
                                setData={setCrmData}
                                say={say}
                                onRemove={(id) => { setRemoved((r) => [...r, id]); setActiveWorkFlow(""); setSelectedUserDetails(""); }}
                                goTo={handleClick}
                                bar={{ trackRef: scrollRefH, canLeft: canScrollLeftH, canRight: canScrollRightH, onLeft: scrollLeftH, onRight: scrollRightH }}
                                persona={
                                  <div className={styles.Sdkwb7}>
                                    <Persona
                                      name={currentTab.name}
                                      secondaryText={
                                        <span style={{ fontWeight: 500 }}>
                                          {currentTab.role || "Contact"} <span style={{ fontSize: '1.2em', fontWeight: 'bold' }}>•</span> {currentTab.company}
                                        </span>
                                      }
                                      avatar={{
                                        image: {
                                          src: "https://res-1.cdn.office.net/files/fabric-cdn-prod_20230815.002/office-ui-fabric-react-assets/persona-male.png",
                                        },
                                        color: "colorful"
                                      }}
                                    />
                                  </div>
                                }
                              />
                            </div>
                          </div>
                        )}

                        <div className={`${styles.Hret}`}></div>
                      </>
                      </div>
                    )}                 
                  
                  {activeTab === "Dashboard" && (
                      <div className={styles.YtRwTBN}>
                        <CrmDashboard />
                      </div>
                    )}
                  {activeTab === "All records" && (
                    <div className={styles.crShell}>
                      <div className={styles.crHost}>
                        <CrmExplorer initialKey={explorerKey} />
                      </div>
                    </div>
                  )}
                  {activeTab !== "Sales accelerator" && activeTab !== "Dashboard" && activeTab !== "All records" && (
                    <div className={styles.crShell}>
                      <div className={styles.crHost}>
                        {!wantLive ? (
                          <div className={ex.cxBanner} role="status">
                            {be ? "Sign in to see your CRM data. This screen shows live records from the CRM service only." : "The CRM service is not connected. Wrap this page in the CRM backend provider and sign in."}
                          </div>
                        ) : liveStatus !== "ready" ? (
                          <>
                            <div className={`${ex.cxBanner} ${liveStatus === "error" ? ex.cxBannerErr : ""}`}>
                              {liveStatus === "error" ? `Could not load from the CRM service. ${liveError ?? ""}` : "Loading from the CRM service…"}
                              {liveStatus === "error" && <Button size="small" onClick={() => setRetry((n) => n + 1)}>Retry</Button>}
                            </div>
                            {liveStatus !== "error" && <Skeleton tiles rows={9} cols={6} />}
                          </>
                        ) : (
                          <>
                            {wantLive && be && be.state !== "connected" && (
                              <MessageBar intent="info" role="status">
                                <MessageBarBody>
                                  <MessageBarTitle>Live updates are {be.state === "off" ? "off" : be.state}</MessageBarTitle>
                                  Changes made by others may not show until the connection returns.
                                </MessageBarBody>
                              </MessageBar>
                            )}
                            <CrmPage tab={activeTab} data={crmData} setData={setCrmData} say={say} live={live} openExplorer={openExplorer} />
                          </>
                        )}
                      </div>
                    </div>
                  )}
                  {toast && (
                    <div className={`${styles.crToast} ${toast.err ? styles.crToastErr : ""}`} role="status" aria-live="polite">
                      {toast.msg}
                    </div>
                  )}
                </div>
              </div>

            </div>

          </div>
        </div>

      </div>
      {/* forms  */}
      <div className="Manage-contact-ui83j">
        {isContactFormVisible &&
          <ContactFormCRM onClose={handleCotactClosed} />
        }
      </div>
    </>
  )
}

/** Supplies the REST client and the SignalR connection to everything inside CRM. */
const CRMWithBackend: React.FC = () => {
  const read = () => { try { return localStorage.getItem("authToken"); } catch { return null; } };
  const [token, setToken] = useState<string | null>(read);
  useEffect(() => {
    const id = window.setInterval(() => setToken((t) => { const n = read(); return n === t ? t : n; }), 3000);
    return () => window.clearInterval(id);
  }, []);
  return <CrmBackendProvider token={token}><CRM /></CrmBackendProvider>;
};

export default CRMWithBackend;